import type {
  FormStateValue,
  ReportingModelDataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { formStateSingles, formStateValueToKeys } from '@de/utils/form-state';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useActionDispatcher } from '../action-resolver/ActionDispatcher';
import { resolveActions } from '../action-resolver/resolveActions';
import {
  hasMissingTemplatePath,
  resolveBindings,
} from '../binding-resolver/resolveBindings';
import { isBindDeclaration } from '../binding-resolver/isBindDeclaration';
import { RESERVED_BIND_SOURCES } from '../binding-resolver/reservedRoots';
import { useDataSourceManager } from '../../providers/DataSourceManager';
import {
  useItemScope,
  useItemScopeIndexPath,
  useItemScopeSection,
} from '../../providers/ItemScopeContext';
import {
  PublishedScopeProvider,
  useOptionalPublishedScope,
  usePublishedScope,
} from '../../providers/PublishedScopeProvider';
import { useFormScope } from '../../providers/FormScopeContext';
import { useRoute } from '../../providers/RouteProvider';
import { warnOnce } from '../warnOnce';
import { scopeContentSignature } from '../published-scope/scopeContentSignature';
import { scopeReadBy } from '../published-scope/scopeReadBy';
import {
  Trackable,
  isProductTrackingDisabled,
  markHostFieldViewConsumed,
  type TrackingMeta,
} from '../../components/Trackable/Trackable';
import { buildStepCorrelationOverride } from '../../components/Trackable/buildStepCorrelationOverride';
import {
  mergeProductCustomDimensions,
  type ProductIdentityContext,
} from '../../components/Trackable/buildProductIdentity';
import {
  useTracking,
  type TrackingContextValue,
} from '../../providers/TrackingProvider';
import { useSubmittedProgramsOptional } from '../../providers/SubmittedProgramsProvider';
import type { ResolvedDataSource } from '../../providers/DataSourceManager.types';
import {
  ACTION_PREFIX,
  ADAPTER_ROWS_BIND_KEY,
  ADAPTER_ROWS_PROP,
  BIND_PREFIX,
  DEFAULT_EVENT_PROP_NAMES,
  ON_SOURCE_CHANGE_EVENT,
  isSyntheticActionEvent,
} from './constants';
import {
  FieldCheckErrorHold,
  holdsFieldCheckUntilRevealed,
} from './hold-field-error';
import { ChartFrame } from '@highereducation/hen-ui';
import { inferenceCalloutAdapter } from '../render-adapters/adapters/inferenceCalloutAdapter';
import { dataTableAdapter } from '../render-adapters/adapters/dataTableAdapter';
import type { DataTableColumnDef } from '../render-adapters/types';
import {
  buildDataTableColumns,
  dataTableHeaderKey,
} from '../render-adapters/presentation/dataTableColumns';
import { resolveFrameCopy } from '../render-adapters/presentation/frameCopy';
import { stableStringify } from '../data-sources/dataSourceCache';
import { resolveBoundStatus } from '../boundStatus/resolveBoundStatus';
import { applyTransformSteps, interpolateTransformSteps } from '../transforms';
import type { TransformRow, TransformStep } from '../transforms/types';
import {
  findNonSerializableProps,
  mergeDefinedProps,
  runAdapterProjection,
} from './runAdapterProjection';
import {
  ADAPTER_ERROR_MESSAGE,
  EMPTY_FRAME_TITLE,
  STALE_FRAME_MESSAGE,
  toChartSurface,
} from './boundChartFrame';
import { extractRootNames } from '../interpolation/extractPaths';
import { eventCatalog } from '@highereducation/eventing/catalog';
import {
  checkBinding,
  composeConfiguredEvent,
  hasLifecycleBinding,
  readBindings,
  readDefaults,
  resolveBindingField,
} from '@de/utils/configured-events/index';
import type {
  BindingDefaults,
  BindingExperience,
  BindingFieldSource,
  BindingResolveScope,
  CatalogEventEntry,
  EventBinding,
} from '@de/utils/configured-events/index';
import type { EventContext } from '../createEventingPayloadsDE';
import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import { getByPath } from '../interpolation/getByPath';
import { hasTemplate } from '../interpolation/hasTemplate';
import { RESERVED_TEMPLATE_ROOTS } from '../binding-resolver/reservedRoots';
import {
  LEAD_FIELD_PRESETS,
  isLeadFieldName,
} from '../configurable-step/leadFieldPresets';
import {
  resolveCardEventing,
  sanitizeFieldValue,
  type FieldTrackingMeta,
} from './resolveCardEventing';
import { isPii } from '../isPii';
import type { PathSegment } from '../interpolation/types';
import type { ComponentBindingMeta, HtmlSinkDescriptor } from './types';
import type { Step } from '../../types/types';
import {
  newRelicNoticeError,
  newRelicPageAction,
} from '@de/utils/newRelicActions';
import { useHostSeededFormKeys } from '../../providers/HostSeededFormKeys';
import { reconcileHostSeededOptions } from './reconcileHostSeededOptions';
import {
  isTextEntryFieldType,
  useFieldInputDebounce,
  type ScheduleFieldInput,
} from './fieldInputDebounce';
import {
  evaluateShowOn,
  evaluateVisibilityWhen,
  parseVisibilityMeta,
} from './visibility';
import {
  readScrollPointName,
  SCROLL_POINT_ATTRIBUTE,
  ScrollPointFallback,
} from './scroll-point';
import { CssBreakpointVisibility } from './CssBreakpointVisibility';
import { useActiveVisibilityBreakpoints } from '@de/hooks/useActiveVisibilityBreakpoints';
import { RichText } from '../../components/RichText';
import {
  isDefaultOnHostPolicy,
  requiresExplicitNodeEnable,
  type TrackingHostPolicy,
} from '../../providers/TrackingHostContext';
import { buildHostElementEventOverride } from '../configurable-step/resolveElementFields';
import {
  buildHostFieldEventContext,
  hasCommittedHostFieldValue,
  normalizeHostFieldValue,
} from '../configurable-step/hostFieldContract';
import {
  mintHostElementEventCorrelationId,
  mintHostElementLifecycleCorrelation,
} from '../hostElementCorrelation';

const VISIBILITY_META_KEY = '_meta.visibility';

/**
 * `rows` is the adapter's input. Callout also reads heading, empty copy,
 * error copy, and the item limit from the same bag, then turns them into
 * `label`, `footer`, `errorContent`, and a sliced list. Those settings are
 * not Callout props; leaving them in the bag lands them on the root element.
 */
function omitProps(
  props: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  const names = Object.keys(props);
  for (let index = 0; index < names.length; index += 1) {
    const name = names[index];
    if (keys.indexOf(name) === -1) next[name] = props[name];
  }
  return next;
}

function dropAdapterInput(
  props: Record<string, unknown>,
  stripCalloutSettings: boolean,
): Record<string, unknown> {
  const withoutRows = omitProps(props, [ADAPTER_ROWS_PROP, 'transforms']);
  if (!stripCalloutSettings) return withoutRows;
  return omitProps(withoutRows, [
    'heading',
    'emptyCopy',
    'errorCopy',
    'itemLimit',
  ]);
}

function omitFrameProps(
  props: Record<string, unknown>,
): Record<string, unknown> {
  return omitProps(props, [
    'title',
    'description',
    'dynamicTitle',
    'staleAfterMs',
  ]);
}

function boundRowsSourceName(
  rawProps: Record<string, unknown>,
): string | undefined {
  const flat = sourceNameFromBinding(rawProps[ADAPTER_ROWS_BIND_KEY]);
  if (flat) return flat;
  const bind = rawProps._bind;
  if (typeof bind !== 'object' || bind === null || Array.isArray(bind)) {
    return undefined;
  }
  return sourceNameFromBinding((bind as Record<string, unknown>).rows);
}

function sourceNameFromBinding(value: unknown): string | undefined {
  return readSourceName(value) ?? templateSourceName(value);
}

function templateSourceName(value: unknown): string | undefined {
  if (typeof value !== 'string' || !hasTemplate(value)) return undefined;
  const roots = extractRootNames(value);
  for (let index = 0; index < roots.length; index += 1) {
    const root = roots[index];
    if (root && !RESERVED_TEMPLATE_ROOTS.has(root)) return root;
  }
  return undefined;
}

function readSourceName(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const source = (value as { source?: unknown }).source;
  return typeof source === 'string' && source.length > 0 ? source : undefined;
}

/**
 * Cheap pre-parse check: does raw `_meta.visibility` carry a non-empty
 * `showOn`? Used to gate the breakpoint subscription without running the
 * full visibility parser before hooks.
 */
function rawVisibilityNeedsShowOn(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return false;
  }
  const showOn = (raw as Record<string, unknown>).showOn;
  return Array.isArray(showOn) && showOn.length > 0;
}

/**
 * Normalize an unknown throw into an `Error` and report it through the
 * shared New Relic seam. The event-handler safety-net catches below swallow
 * throws purely to keep the experience alive (SAD §14); routing them here
 * makes that otherwise-silent degradation visible in monitoring while the
 * seam still logs to the console in dev / when the agent is absent.
 */
function reportBoundRenderError(error: unknown, source: string): void {
  newRelicNoticeError(
    error instanceof Error ? error : new Error(String(error)),
    {
      source,
    },
  );
}

const BOUND_RENDER_FLAG = '__edu_bound';

/**
 * Extract the `string` we want to store in `formState[formKey]` from
 * whatever the HEN UI component passed to its handler.
 *
 * Supported shapes (in priority order):
 *   1. `event.target.value`    — native React `onChange(event)`
 *   2. `event.currentTarget.value`
 *   3. `firstArg` itself       — controlled `onChange(value)` pattern
 *
 * **Footgun.** A non-event object that happens to expose a `.target` or
 * `.currentTarget` key (for example a HEN UI catalog component that
 * calls `onChange({ target: 'someRef' })` with `target` as plain data,
 * not a SyntheticEvent target) will be misclassified as a SyntheticEvent
 * and the wrong field will land in `formState`. Catalog wrappers in
 * that shape **must** opt out by supplying
 * {@link ComponentBindingMeta.formKeyValueTransform}.
 */
function defaultExtractValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    const evt = value as {
      target?: { value?: unknown };
      currentTarget?: { value?: unknown };
    };
    if (
      evt.target &&
      typeof evt.target === 'object' &&
      evt.target.value !== undefined
    ) {
      return String(evt.target.value);
    }
    if (
      evt.currentTarget &&
      typeof evt.currentTarget === 'object' &&
      evt.currentTarget.value !== undefined
    ) {
      return String(evt.currentTarget.value);
    }
  }
  return String(value);
}

/**
 * Narrow `finalProps.options` to the `{ value }[]` shape that
 * `transform: 'options'` produces, for the single-option auto-select
 * guard. Mirrors `isOptionsArray` in `DslConfig.tsx` (kept local so
 * the enhancer stays decoupled from the Puck render module), but only
 * requires `value` — that is the field the auto-select commits.
 */
const KEEPS_OPTIONS_WHILE_LOADING = new Set(['MultiSelect']);

/**
 * The options list follows its own source. `loading` on the resolved
 * bindings is true while any referenced source is pending, so a second
 * bound prop must not keep a list that has already settled empty.
 */
function optionsSourcePending(
  rawProps: Readonly<Record<string, unknown>>,
  sources: Readonly<Record<string, { readonly status?: string } | undefined>>,
): boolean {
  const binding = rawProps[`${BIND_PREFIX}options`];
  if (
    !isBindDeclaration(binding) ||
    RESERVED_BIND_SOURCES.has(binding.source)
  ) {
    return false;
  }
  return sources[binding.source]?.status === 'pending';
}

/**
 * While the options source is in flight, `options` resolves to `[]` and an
 * open menu collapses. A MultiSelect that opted in keeps the list already
 * shown until that source settles, including when the settled list is empty.
 * The retained list is returned for the caller to store after commit.
 */
function optionsWhileLoading(
  componentName: string,
  enabled: boolean,
  optionsPending: boolean,
  options: unknown,
  retained: unknown,
): { readonly options: unknown; readonly retain: unknown } {
  const incoming = Array.isArray(options) ? options : undefined;
  const previous = Array.isArray(retained) ? retained : undefined;
  const hold =
    enabled &&
    KEEPS_OPTIONS_WHILE_LOADING.has(componentName) &&
    optionsPending &&
    incoming !== undefined &&
    incoming.length === 0 &&
    previous !== undefined &&
    previous.length > 0;

  if (hold) return { options: previous, retain: previous };

  if (incoming !== undefined && (!optionsPending || incoming.length > 0)) {
    return { options, retain: incoming };
  }
  return { options, retain: retained };
}

function isResolvedOptionsArray(
  value: unknown,
): value is ReadonlyArray<{ value: unknown }> {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        entry !== null && typeof entry === 'object' && 'value' in entry,
    )
  );
}

/**
 * Wrap a HEN UI component so that DSL-level `_bind.*`, `_action.*`,
 * `_meta.*`, and `formKey` props are resolved into concrete React
 * props and event handlers before reaching the underlying render.
 *
 * Flow per render (child SAD §5.2):
 *   1. Read DSM ({@link useDataSourceManager}), action dispatcher
 *      ({@link useActionDispatcher}), and repeater item
 *      ({@link useItemScope}) from context.
 *   2. Run the binding resolver — strips prefixed props, resolves
 *      `_bind.*`, collects `_meta.*`, detects loading and aggregate
 *      data-source status.
 *   3. Run the action resolver — emits one handler per `_action.{event}`.
 *   4. When `formKey` is present, wrap the first available event
 *      handler (default `onChange`, `onSelect`) to also push the
 *      emitted value into `updateFormState`.
 *   5. Wrap the rendered component in {@link Trackable} so DSL nodes
 *      participate in the entry-point + in-flow event sequence. The
 *      resolved `_meta.tracking.*` slice authored on the DSL node is
 *      merged on top of the optional `trackingDefault` argument so
 *      per-type defaults (e.g. every `Select` is a `field`) take
 *      effect without forcing every DSL config to author them,
 *      while per-instance overrides still win — metadata-driven, no
 *      per-component code.
 *
 * The returned component is marked with `__edu_bound = true` so
 * {@link applyBindings} can skip double-wrapping when a config is
 * passed through the enhancer twice.
 */

/** A sent request is generating. A source that has not started stays pending. */
function calloutPhaseWhileLoading(
  names: ReadonlySet<string>,
  sources: Readonly<Record<string, ResolvedDataSource>>,
): 'pending' | 'generating' {
  let generating = false;
  names.forEach((name) => {
    const source = sources[name];
    if (source?.status === 'pending' && source.dispatched === true) {
      generating = true;
    }
  });
  return generating ? 'generating' : 'pending';
}

/**
 * Internal handler key for a tracking-only click that `Trackable` listens
 * for on its wrapper. Never passed to the component.
 */
const TRACKING_WRAPPER_CLICK_SLOT = 'trackingWrapperClick';

/**
 * Set on the rendered control when a configured ElementClicked will send.
 * The page host's click listener skips that element, so the configured
 * event is the only ElementClicked.
 */
const CONFIGURED_ELEMENT_CLICK_ATTR = 'data-edu-configured-element-click';

function isSupportedClickHandler(
  name: string,
  clickPropName: string,
  eventPropNames: readonly string[],
): boolean {
  if (name === clickPropName) return true;
  return eventPropNames.includes(name) && name.endsWith('Click');
}

/**
 * `navigation.advanceStep` / `navigation.advanceStepWithoutSubmit` are the
 * two action types that reach `useHandleStepForward`.
 */
const FORWARD_NAVIGATION_ACTION_TYPES = new Set<string>([
  'navigation.advanceStep',
  'navigation.advanceStepWithoutSubmit',
]);

/**
 * Mirrors `resolveActions`'s own `isActionDeclaration` shape check
 * (non-null plain object, non-empty string `type`, `params` either absent
 * or a plain object) without importing its internals — kept in lockstep so
 * a declaration this function accepts is never one `resolveActions` itself
 * would drop as malformed (which would leave `ownsForwardNavigation` true
 * for a click whose `_action.onClick` never actually registers a handler).
 */
function isWellFormedActionDeclaration(
  value: unknown,
): value is Record<string, unknown> {
  if (!isPlainObject(value)) return false;
  if (typeof value.type !== 'string' || value.type.length === 0) return false;
  return value.params === undefined || isPlainObject(value.params);
}

/**
 * Whether `_action.<clickPropName>` declares a navigation action that
 * reaches `useHandleStepForward` on this click. Read directly off
 * `rawProps` rather than the resolved `actionHandlers` map — that map only
 * exposes an opaque dispatch closure per `resolveActions`, with no way to
 * recover which action type it wraps. An array declaration is all-or-
 * nothing, same as `resolveActions`: one malformed entry drops the whole
 * list rather than just that entry.
 */
function declaresForwardNavigation(
  rawProps: Readonly<Record<string, unknown>>,
  clickPropName: string,
): boolean {
  const declared = rawProps[`${ACTION_PREFIX}${clickPropName}`];
  const list = Array.isArray(declared)
    ? declared.length > 0 && declared.every(isWellFormedActionDeclaration)
      ? declared
      : []
    : isWellFormedActionDeclaration(declared)
      ? [declared]
      : [];
  return list.some(
    (entry) =>
      isPlainObject(entry) &&
      FORWARD_NAVIGATION_ACTION_TYPES.has(entry.type as string),
  );
}

export function createBoundRender<P extends object>(
  Component: React.ComponentType<P>,
  meta?: ComponentBindingMeta,
  trackingDefault?: TrackingMeta,
  componentType?: string,
): React.ComponentType<Record<string, unknown>> {
  const maybeBound = Component as React.ComponentType<P> & {
    [BOUND_RENDER_FLAG]?: boolean;
  };
  if (maybeBound[BOUND_RENDER_FLAG] === true) {
    return Component as unknown as React.ComponentType<Record<string, unknown>>;
  }

  // The DSL config hands over a `render` wrapper with no display name.
  // The registry key is the hen-ui type that field checks key off.
  const componentName =
    componentType || Component.displayName || Component.name || '';
  const eventPropNames =
    meta?.eventPropNames !== undefined
      ? meta.eventPropNames
      : DEFAULT_EVENT_PROP_NAMES;
  const formKeyValueTransform =
    meta?.formKeyValueTransform ?? defaultExtractValue;
  const staticProps = meta?.staticProps;
  const valuePropName = meta?.valuePropName;
  const formStateValueTransform = meta?.formStateValueTransform;
  // Click slot the binding enhancer wires for product click
  // dispatch. Defaults to `onClick`; catalogs whose click affordance
  // lives on a non-standard slot (a custom CTA exposing `onTrigger`,
  // etc.) override via `ComponentBindingMeta.clickPropName`.
  const clickPropName = meta?.clickPropName ?? 'onClick';
  const trackingOnlyClickOnWrapper = meta?.trackingOnlyClickOnWrapper === true;
  const outboundUrlPropPath = meta?.outboundUrlPropPath;
  // Opt-in single-option auto-select (cascading-select convenience).
  // Off by default so only single-select dropdowns get the behaviour.
  const autoSelectSingleOption = meta?.autoSelectSingleOption === true;
  const seedDefaultValue = meta?.seedDefaultValue === true;
  const omitDefaultValueWhenBound = meta?.omitDefaultValueWhenBound === true;
  const seedAwaitsTemplatePaths = meta?.seedAwaitsTemplatePaths === true;
  const formatLeadField = meta?.formatLeadField === true;
  // Controlled-state descriptor (ADR Seam 1 / F1). Closure-captured at
  // wrap time so the per-tuple `useState` loop inside `BoundComponent`
  // has a CONSTANT length for a given component type — that is what
  // keeps the hook injection Rules-of-Hooks safe. Empty array for
  // components that declare none, so they run zero extra hooks.
  const controlledState = meta?.controlledState ?? [];
  // Props whose resolved string value is untrusted rich HTML (CMS
  // blurb). Normalized to `{ prop, variant }` and closure-captured at
  // wrap time so the post-resolution sink loop below is a plain non-hook
  // pass over `finalProps` — Rules-of-Hooks safety is unaffected. The
  // render variant is surfaced per designation (via the descriptor)
  // rather than hardcoded in the loop. Empty for components that
  // designate none.
  const htmlProps: readonly HtmlSinkDescriptor[] = (meta?.htmlProps ?? []).map(
    (entry) => (typeof entry === 'string' ? { prop: entry } : entry),
  );
  // Render-adapter projection (Gap 1). Closure-captured at wrap time like
  // every other meta field, so whether a component has one is a property of
  // its registration rather than of an individual node's props.
  const adapter = meta?.adapter;
  const statusFrame = meta?.statusFrame === true;
  // Captured at wrap time for the same reason: whether a component is handed
  // the source lifecycle is a property of its registration.
  const forwardsLifecycle = meta?.lifecycleProps !== 'omit';
  const forwardsSourcesPending = meta?.forwardSourcesPending === true;
  const filterAuthoredProps = meta?.filterAuthoredProps;
  const collectActionsAs = meta?.collectActionsAs;
  const outputPolicy = meta?.outputPolicy;
  const acceptsClickHandler = (name: string): boolean =>
    isSupportedClickHandler(name, clickPropName, eventPropNames);
  // applyBindings stamps `authoredDslType` on the wrapper this function
  // returns. The inner component never receives that property.
  const authoredBlockType: { current?: string } = {};

  const BoundComponent: React.FC<Record<string, unknown>> = (rawProps) => {
    const dsm = useDataSourceManager();
    const hostSeededFormKeys = useHostSeededFormKeys();
    const dispatcher = useActionDispatcher();
    const item = useItemScope();
    const itemIndexPath = useItemScopeIndexPath();
    const itemSection = useItemScopeSection();
    const formName = useFormScope();
    const route = useRoute();
    const publishedScope = useOptionalPublishedScope();
    const ancestorScope = usePublishedScope();
    const { componentProps: sourceStrippedProps } =
      splitNodeDataSource(rawProps);
    const nodeSourceId = readNodeSource(rawProps, ancestorScope).id;
    const boundSources = useMemo(
      () =>
        nodeSourceId ? withSelfSource(dsm.sources, nodeSourceId) : dsm.sources,
      [dsm.sources, nodeSourceId],
    );
    const tracking = useTracking();
    // Optional submitted-programs registry — resolves the results-card click
    // `actionOutcome` (`application` once submitted, else `explore`).
    const submittedPrograms = useSubmittedProgramsOptional();

    // Local state backing for declared controlled-state tuples (Seam 1).
    // Fixed-position hook: `controlledState` is closure-captured from
    // `meta` at wrap time, so this loop runs the SAME number of hooks on
    // every render of a given component type — Rules-of-Hooks safe. Do
    // NOT make these conditional. The injected value/setter are applied
    // to `finalProps` further down, after prop resolution.
    const controlledStates = controlledState.map(
      // eslint-disable-next-line react-hooks/rules-of-hooks
      (tuple) => useState<unknown>(tuple.default),
    );

    /**
     * No `useMemo` here intentionally. Puck reconstructs `rawProps` on
     * every render so a memo keyed by `rawProps` would never hit. The
     * perf budget test (`perf.test.ts`) holds the resolver to 25×1000
     * renders < 250ms, which is sufficient — adding a memo would only
     * provide value if the host stabilises `rawProps` itself, which
     * Puck does not.
     */
    const bindingContext = {
      sources: boundSources,
      formState: dsm.formState,
      flowContext: dsm.flowContext,
      item,
      route,
      scope: publishedScope,
    };
    const {
      props,
      loading,
      dataSourceStatus,
      formKey,
      autoSelectFirstOption,
      keepOptionsWhileLoading,
      meta: resolvedMeta,
      referencedSources,
    } = resolveBindings(
      filterAuthoredProps
        ? filterAuthoredProps(sourceStrippedProps)
        : sourceStrippedProps,
      bindingContext,
    );

    // Hoisted here (above `useOnSourceChange`) because the watcher
    // pins its settled-snapshot store to this same identity to
    // survive ancestor remounts (Strict Mode + DSM-driven re-render
    // churn tear down and re-create the bound subtree on every
    // settle). The full doc on composition + skip conditions lives
    // beside `<Trackable>` below; this hoist intentionally shares
    // the **identical** key so both dedup surfaces stay aligned —
    // any future change to one must mirror in the other.
    const puckNodeId =
      typeof rawProps.id === 'string' && rawProps.id.length > 0
        ? rawProps.id
        : undefined;
    const dedupNodeId = puckNodeId
      ? itemIndexPath !== undefined
        ? `${puckNodeId}::${itemIndexPath}`
        : puckNodeId
      : undefined;
    const stableKey =
      tracking && dedupNodeId
        ? tracking.composeDedupKey(dedupNodeId)
        : undefined;

    // Resolve the merged tracking meta UP FRONT so the event-handler
    // composition below can dispatch `FieldInputted` from the formKey
    // capture wrapper when this node is a tracked field. Memoize so
    // `Trackable` receives a stable `meta` prop reference across
    // re-renders driven by DSM `formState` updates — without this,
    // every formState change rebuilds `trackingMeta` and churns
    // dependent effects in `Trackable`. A stable meta reference is
    // one half of the fire-once defense alongside the module-level
    // WeakSet inside `Trackable`.
    const resolvedTrackingSlice =
      resolvedMeta &&
      isPlainObject(resolvedMeta.tracking) &&
      resolvedMeta.tracking !== null
        ? (resolvedMeta.tracking as Readonly<Record<string, unknown>>)
        : undefined;
    const resolvedTrackingKey = useMemo(
      () =>
        resolvedTrackingSlice ? JSON.stringify(resolvedTrackingSlice) : '',
      [resolvedTrackingSlice],
    );
    const derivedOutboundUrl = outboundUrlPropPath
      ? readStringPropPath(props, outboundUrlPropPath)
      : undefined;
    // Author `_meta.tracking.location`, captured before the registry default is
    // merged so the section refinement below can leave an author override alone.
    const authoredLocation =
      resolvedTrackingSlice &&
      typeof resolvedTrackingSlice.location === 'string'
        ? resolvedTrackingSlice.location
        : undefined;
    const trackingMeta = useMemo(
      () =>
        applySectionLocation(
          enrichTrackingOutboundUrl(
            mergeTrackingMeta(
              trackingDefault,
              omitScrollPoint(resolvedMeta),
              formKey,
            ),
            derivedOutboundUrl,
          ),
          itemSection,
          authoredLocation,
        ),
      // `resolvedTrackingKey` collapses object identity churn on
      // `resolvedMeta` (rebuilt every render by the binding resolver)
      // to a stable string when the underlying tracking slice is
      // unchanged. `trackingDefault` is closure-stable. `formKey` is
      // a string from the resolver — only changes when the DSL config
      // itself changes, which is exactly when we want a fresh merge.
      // `itemSection` / `authoredLocation` re-merge when either changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        resolvedTrackingKey,
        formKey,
        derivedOutboundUrl,
        itemSection,
        authoredLocation,
      ],
    );
    const isDefaultOnHost = isDefaultOnHostPolicy(tracking?.host?.policy);
    const trackingFieldMeta = requiresExplicitNodeEnable(tracking?.host)
      ? null
      : readTrackingFieldMeta(trackingMeta, isDefaultOnHost);
    // Field values are redacted when any lead field or field name the node
    // carries is PII. Event identity keeps `trackingFieldMeta`.
    const sanitizeFieldMeta = resolveSanitizeFieldMeta(
      trackingFieldMeta,
      resolvedMeta,
      formKey,
    );
    const hostFieldSlice =
      isDefaultOnHost &&
      isPlainObject(trackingMeta.tracking) &&
      trackingMeta.tracking.kind === 'field'
        ? trackingMeta.tracking
        : undefined;
    const optionsSourceKey =
      typeof hostFieldSlice?.optionsSourceKey === 'string'
        ? hostFieldSlice.optionsSourceKey
        : undefined;
    const optionSourceStatus = optionsSourceKey
      ? dsm.sources[optionsSourceKey]?.status
      : undefined;
    const rawFieldFormValue =
      formKey !== undefined ? dsm.formState[formKey] : undefined;
    const hostFieldFormValue = isDefaultOnHost
      ? normalizeHostFieldValue(rawFieldFormValue)
      : rawFieldFormValue;
    const fieldViewReady =
      optionsSourceKey === undefined ||
      ((optionSourceStatus === 'resolved' || optionSourceStatus === 'error') &&
        hasCommittedHostFieldValue(hostFieldFormValue));
    // Click-dispatch metadata for `kind: 'product'`. Pre-resolved
    // here (rather than inline in `composeEventHandlers`) so the
    // composer stays free of meta-shape inspection and the dispatch
    // call site has a typed handle. Returns `null` for every other
    // `kind` so the click slot only gets the dispatch wrapper when
    // the admin contract requires it.
    //
    // `kind: 'submit'` is intentionally not handled here: DSL submit
    // CTAs route their click through `_action.onClick` (e.g.
    // `flow.forward`) into `handleStepForward`, and the
    // provider-owned `useForwardStepEventFiring` emits the
    // spec-compliant `FormStarted` / `ProductClicked` /
    // `FormContinued` triple.
    const trackingClickMeta = readTrackingClickMeta(
      trackingMeta,
      tracking?.host?.policy,
    );
    const productClickMeta =
      trackingClickMeta?.kind === 'product' ? trackingClickMeta : null;

    // Per-card eventing decisions (product identity + results-card click
    // `actionOutcome` + PII-safe field view value) resolved up front in one pass.
    const authoredInputType =
      typeof props.type === 'string' ? props.type : undefined;
    const { productContext, resultActionOutcome, fieldViewValue } =
      resolveCardEventing({
        item,
        itemIndexPath,
        clickKind: productClickMeta?.kind,
        clickFormatSubtype: productClickMeta?.formatSubtype,
        fieldMeta: sanitizeFieldMeta,
        formKey,
        formStateValue: hostFieldFormValue,
        currentStep: tracking?.eventContext?.currentStep as Step | undefined,
        inputType: authoredInputType,
        isProgramSubmitted: submittedPrograms
          ? (programId) => submittedPrograms.isProgramSubmitted(programId)
          : undefined,
        productFields: tracking?.productFields,
        customDimensionMappings: tracking?.customDimensionMappings,
      });

    // Runtime-resolved `navigation.linkout` param fallbacks, sourced from the
    // SAME inputs — and with the SAME precedence — the `ProductClicked` payload
    // uses so the Linkout / HLF conversion is classified AND attributed
    // identically to the click (Mobius parity). Computed AFTER
    // `resolveCardEventing` so it can read the merged `trackingClickMeta` and
    // the item-derived `productContext`; `resolveActions` (below) applies it to
    // the linkout action only. Covers the classification (`formatType` /
    // `formatSubtype`) and the per-click `correlationId` — both values the DSL
    // cannot author as static literals.
    const linkoutParamFallbacks = resolveLinkoutParamFallbacks(
      productClickMeta,
      productContext,
      tracking?.eventContext?.formViewedCorrelationId,
      // `country` / `state` come from single-valued keys (`geolocation`,
      // `countryCode`), so this consumer is defined over strings only.
      formStateSingles(dsm.formState),
    );

    const actionHandlers = resolveActions(rawProps, {
      dispatcher,
      sources: boundSources,
      formState: dsm.formState,
      readFormState: dsm.getFormState,
      flowContext: dsm.flowContext,
      item,
      formName,
      route,
      scope: publishedScope,
      // Curated component-self snapshot exposed to `_action.params`
      // via the `{{this.<key>}}` interpolation root. Today only
      // `formKey` is whitelisted — the cascading-select reset
      // pattern needs `params.keys: ["{{this.formKey}}"]` and we
      // explicitly do NOT want to leak `_bind.*` declarations or
      // resolved values through this surface (see the
      // `componentSelf` doc on `ActionResolverContext`).
      componentSelf: { formKey },
      linkoutParamFallbacks,
      outputPolicy,
    });

    // Whether THIS click also carries `_action.onClick: navigation.advanceStep`
    // (or `…advanceStepWithoutSubmit`) — i.e. whether this click is the one
    // `useHandleStepForward` will process. Only that click's product-click
    // decision can safely be signalled to the entry forward-step firer; a
    // sibling control's click (e.g. a card with its own configured
    // ProductClicked binding but no navigation action) never reaches the
    // firer and must not write the shared suppression ref.
    const ownsForwardNavigation = declaresForwardNavigation(
      rawProps,
      clickPropName,
    );

    // Synthetic events (today: `onSourceChange`) are fired by the
    // binding-enhancer itself, not by the underlying HEN UI
    // component. Pull their handlers out of `actionHandlers` BEFORE
    // composing event handlers so they (1) don't get wired onto
    // a real React event prop the component doesn't recognise, and
    // (2) are available to the `useOnSourceChange` watcher below.
    const syntheticHandlers = extractSyntheticHandlers(actionHandlers);

    useOnSourceChange({
      handler: syntheticHandlers[ON_SOURCE_CHANGE_EVENT],
      referencedSources,
      sources: boundSources,
      stableKey,
    });

    /**
     * Build the final prop bag in three phases so authored event
     * handlers compose with `_action.*` and `formKey` instead of being
     * silently overwritten — compose **all** callable sources, not
     * just one:
     *   1. Static props supplied by the catalog wrapper (lowest
     *      priority).
     *   2. Resolved `_bind.*` props (and raw passthrough props).
     *   3. Composed event handlers — for each event slot we compose,
     *      in order, the `staticProps.<event>` handler, the
     *      resolved/raw `<event>` handler, the `_action.<event>`
     *      handler, then the optional `formKey` capture wrapper.
     *      When the formKey capture wrapper fires AND this node is a
     *      tracked field (from `trackingMeta.tracking.kind ===
     *      'field'`) AND a `<TrackingProvider>` is mounted, the
     *      capture also dispatches `triggerFieldEvents('FieldInputted',
     *      …)` so the user's selection produces a "field selected"
     *      event on the wire. All callable sources run with the
     *      original event arguments; each is wrapped in a try/catch
     *      so a thrower cannot stop later sources from firing.
     *
     *   4. Click dispatch — when `trackingMeta.tracking.kind ===
     *      'product'` AND a `<TrackingProvider>` is mounted, the
     *      click slot (`clickPropName`, default `onClick`) is queued
     *      unconditionally and the composed handler also dispatches
     *      `triggerProductClickedEvent` with the metadata authored on
     *      `_meta.tracking`. The slot is queued even when no
     *      authored handler / `_action.onClick` exists so a
     *      tracking-only Button still emits a wire event on click.
     *
     *      `kind: 'submit'` does NOT install a dedicated click
     *      dispatcher here — see the `trackingClickMeta` comment
     *      above. Submit CTAs route through `_action.onClick`
     *      (e.g. `flow.forward`) → `useHandleStepForward` → the
     *      provider's `useForwardStepEventFiring` for spec-compliant
     *      `FormStarted` / `ProductClicked` / `FormContinued`
     *      orchestration.
     */
    const leadFieldFormat = formatLeadField
      ? leadFieldFormatter(resolvedMeta?.leadFieldAssociated)
      : undefined;
    const clickBindingScope: BindingResolveScope = {
      item,
      sources: bindingSourceData(boundSources),
      experience: {
        ...(tracking?.configuredEventContext ?? {}),
        ...(puckNodeId ? { blockId: puckNodeId } : {}),
        ...(authoredBlockType.current
          ? { blockType: authoredBlockType.current }
          : {}),
      },
    };
    const hasTracking = tracking != null;
    const clickDefaults = useMemo(
      () => (hasTracking ? readDefaults(resolvedTrackingSlice) : {}),
      // The slice object is rebuilt with props. The key is its content.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [hasTracking, resolvedTrackingKey],
    );
    const clickBindings = useMemo(
      () =>
        hasTracking
          ? readyClickBindings(resolvedTrackingSlice, acceptsClickHandler)
          : [],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [hasTracking, resolvedTrackingKey, acceptsClickHandler],
    );
    const configuredEventContext = tracking?.configuredEventContext;
    const trackingEventContext = tracking?.eventContext;
    const configuredElementClick = useMemo(() => {
      if (!hasTracking || !tracking) return false;
      return clickBindings.some((ready) => {
        if (ready.binding.event !== 'ElementClicked') return false;
        try {
          return (
            composeReadyClick(
              ready,
              clickBindingScope,
              clickBindingScope.experience,
              (trackingEventContext ?? {}) as EventContext,
            )?.name === 'ElementClicked'
          );
        } catch {
          return false;
        }
      });
      // clickBindingScope is rebuilt every render from these inputs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      hasTracking,
      clickBindings,
      item,
      puckNodeId,
      configuredEventContext,
      trackingEventContext,
      boundSources,
      authoredBlockType.current,
    ]);
    const suppressedConfiguredClick = clickBindings.some(
      (ready) => clickDefaults[ready.binding.on] === 'suppress',
    );
    const scheduleFieldInput = useFieldInputDebounce();
    const {
      [TRACKING_WRAPPER_CLICK_SLOT]: trackingWrapperClick,
      ...composedHandlers
    } = composeEventHandlers({
      eventPropNames,
      staticProps,
      resolvedProps: props,
      actionHandlers: collectActionsAs ? {} : actionHandlers,
      formKey,
      formKeyValueTransform,
      formatCapturedValue: leadFieldFormat,
      updateFormState: dsm.updateFormState,
      tracking,
      fieldTrackingMeta: trackingFieldMeta,
      sanitizeFieldMeta,
      scheduleFieldInput,
      hostFieldSlice,
      hostFieldNodeId: puckNodeId,
      hostFieldLabel: typeof props.label === 'string' ? props.label : undefined,
      inputType: authoredInputType,
      clickPropName,
      trackingOnlyClickOnWrapper,
      clickTrackingMeta: trackingClickMeta,
      declaredClickKind:
        resolvedTrackingSlice?.kind === 'element' ||
        resolvedTrackingSlice?.kind === 'product'
          ? resolvedTrackingSlice.kind
          : undefined,
      clickBindings,
      clickDefaults,
      clickBindingScope,
      stableKey,
      productContext,
      resultActionOutcome,
      fieldViewReady,
      ownsForwardNavigation,
    });

    /**
     * Render-adapter projection (Gap 1).
     *
     * Runs only when the component's registration declares an adapter AND the
     * node authored `_bind.rows`. Both halves are required: a registration
     * alone would run the adapter over an undefined row set on every node of
     * that type, and an authored binding alone has no adapter to run.
     *
     * Precedence places the output between static props and authored props,
     * because it is more specific than a catalog default and less specific
     * than what an author wrote on this node. The consequence worth stating:
     * an author can override any field the adapter computes, and does not
     * need the adapter to know the field exists.
     *
     * `rows` itself does not reach the component. It was the adapter's input,
     * and forwarding it alongside the output would hand the component two
     * representations of the same data with nothing saying which is
     * authoritative. Callout settings (`heading`, `emptyCopy`, `errorCopy`,
     * `itemLimit`) are dropped the same way: the adapter already turned them
     * into props Callout understands.
     */
    const adapterApplies =
      adapter !== undefined && ADAPTER_ROWS_BIND_KEY in rawProps;

    /**
     * Not run while the bound source is still in flight. Adapters classify a
     * non-array input as malformed rows, and `_bind.rows` is `undefined`
     * until the first fetch settles — so running one during loading reports a
     * configuration defect for a request that has not failed, and every chart
     * would flash an adapter error on mount.
     *
     * Callout is the exception: its loading state is `state`, not the
     * `loading` prop charts read, and the adapter already maps a pending
     * source onto that state. Skipping it leaves the component on its
     * default ready state for the whole fetch. A request that has been
     * sent is `generating`. The wait before that send stays `pending`.
     */
    const projectLoading =
      adapterApplies && loading && adapter === inferenceCalloutAdapter;
    const labelContext = {
      formState: dsm.formState,
      context: dsm.flowContext,
      item,
    };
    const authoredSteps = Array.isArray(props.transforms)
      ? (props.transforms as readonly TransformStep[])
      : undefined;
    const resolvedSteps =
      adapterApplies && authoredSteps
        ? interpolateTransformSteps(authoredSteps, labelContext)
        : undefined;
    const transformKey = resolvedSteps ? stableStringify(resolvedSteps) : '';
    const rowInput = props[ADAPTER_ROWS_PROP];
    // Step content, not the array identity: a new prop object on every render
    // must not rerun the steps when an unrelated form value changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const transformed = useMemo(() => {
      if (!resolvedSteps || !Array.isArray(rowInput) || loading) {
        return undefined;
      }
      return applyTransformSteps(rowInput as TransformRow[], resolvedSteps);
    }, [rowInput, transformKey, loading]);
    // A skipped step or a null cell is still a drawn chart. The warning is
    // the signal; it does not replace the rows and it is not a frame error.
    if (transformed) {
      for (let index = 0; index < transformed.warnings.length; index += 1) {
        warnOnce('bound-row-transforms', transformed.warnings[index]);
      }
    }
    const transformFailure = transformed?.error
      ? {
          code: 'unknown_transform' as const,
          message: transformed.error.message,
        }
      : undefined;
    const projectionInput =
      transformed && !transformFailure
        ? { ...props, [ADAPTER_ROWS_PROP]: transformed.rows }
        : props;
    const adapterOutcome =
      adapterApplies && !transformFailure && (!loading || projectLoading)
        ? runAdapterProjection(
            adapter as NonNullable<typeof adapter>,
            projectLoading
              ? {
                  ...props,
                  [ADAPTER_ROWS_PROP]: {
                    status: calloutPhaseWhileLoading(
                      referencedSources,
                      boundSources,
                    ),
                  },
                }
              : projectionInput,
            reportBoundRenderError,
          )
        : undefined;

    const { componentProps, scopeOverlay } = splitPublishedScope(props);
    const authoredProps = adapterApplies
      ? dropAdapterInput(componentProps, adapter === inferenceCalloutAdapter)
      : componentProps;

    const finalProps: Record<string, unknown> = {
      ...mergeDefinedProps(staticProps, adapterOutcome?.props, authoredProps),
      ...composedHandlers,
      ...((configuredElementClick || suppressedConfiguredClick) &&
      !meta?.suppressClickOwnershipMarker
        ? { [CONFIGURED_ELEMENT_CLICK_ATTR]: 'true' }
        : {}),
      ...(collectActionsAs ? { [collectActionsAs]: actionHandlers } : {}),
      ...(meta?.passFormKeyToComponent && formKey !== undefined
        ? { formKey }
        : {}),
      ...(forwardsLifecycle
        ? {
            loading:
              typeof props.loading === 'string'
                ? props.loading
                : typeof staticProps?.loading === 'string'
                  ? staticProps.loading
                  : loading,
            dataSourceStatus,
          }
        : {}),
      ...(forwardsSourcesPending ? { sourcesPending: loading } : {}),
    };

    if (adapterApplies) {
      /**
       * A failed adapter is reported as its own state rather than folded into
       * `dataSourceStatus`. "The query returned nothing" and "the query
       * returned rows the adapter could not read" send a reader to different
       * places, and a component that reports the second as the first sends
       * them to the wrong one. Suppressed alongside the rest of the lifecycle
       * when the registration opts out — a component that cannot draw a
       * loading state cannot draw an adapter failure either.
       */
      if (forwardsLifecycle) {
        finalProps.adapterError = transformFailure ?? adapterOutcome?.failure;
      }

      /**
       * The merged object is the one that has to be data all the way down.
       * Adapter output is already held to that by its own contract test, so a
       * guard stopping there is defeated by an author writing a function into
       * the same prop bag -- which is exactly what merging the two objects
       * makes possible. Handlers are excluded because they are legitimately
       * functions.
       *
       * Development only. The walk recurses every row and every key of the
       * projected output, and a bound component re-renders on each DSM form
       * write -- so on a table of a few thousand rows this runs tens of
       * thousands of times per filter interaction, which is the moment a user
       * is already waiting. What it catches is a coding error in an adapter or
       * an authored prop: it is found once, in development, and shipping the
       * check adds nothing a production user can act on.
       */
      if (process.env.NODE_ENV !== 'production') {
        const offenders = findNonSerializableProps(
          Object.fromEntries(
            Object.entries(finalProps).filter(
              ([key]) => !(key in composedHandlers),
            ),
          ),
        );
        if (offenders.length > 0) {
          reportBoundRenderError(
            new Error(
              `Bound props are not serializable at: ${offenders.join(', ')}`,
            ),
            'binding-enhancer/adapter-projection-serializability',
          );
        }
      }
    }

    const declarativeColumns =
      adapterApplies &&
      adapter === dataTableAdapter &&
      Array.isArray(finalProps.columns)
        ? (finalProps.columns as DataTableColumnDef[])
        : undefined;
    const declarativeColumnKey = declarativeColumns
      ? stableStringify(declarativeColumns)
      : '';
    const headerKey = declarativeColumns
      ? dataTableHeaderKey(declarativeColumns, labelContext)
      : '';
    // Column objects are new every render. The string keys are the content
    // that changes a header or a cell, so an unrelated form value keeps the
    // same column array and the same cell functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const renderedColumns = useMemo(() => {
      if (!declarativeColumns) return undefined;
      return buildDataTableColumns(declarativeColumns, labelContext);
    }, [declarativeColumnKey, headerKey]);
    if (renderedColumns) {
      finalProps.columns = renderedColumns;
    }

    /**
     * `formKey` round-trip: when the DSL node sets `formKey` and the
     * catalog meta declares which prop the component reads its value
     * from (`valuePropName`), auto-bind that prop to
     * `formState[formKey]` so the component stays controlled across
     * re-renders. Without this, `formKey` is write-only capture — the
     * user's selection lands in DSM but the value prop is `undefined`
     * on the next render and the displayed selection resets.
     *
     * Precedence:
     *   - An explicit `_bind.<valuePropName>` always wins. The resolver
     *     already wrote that value into `props`.
     *   - Once `formState` has the key, it wins over a static prop.
     *     MultiSelect persists `selectedValues: []` from the catalog
     *     default; treating that array as authoritative made every click
     *     write form state and then render the empty array again.
     *   - Before the key exists, a static prop stays (authored initial
     *     selection). With no static prop, write the transformed
     *     form-state value so the prop is still present.
     *   - A plain authored `''` counts as unset: authoring tools persist
     *     a cleared text field that way, and it must not block the bind.
     *
     * `formStateValueTransform` receives `undefined` when the key is
     * absent so non-string components (Checkbox/Switch/Slider) can
     * pick their own initial-state shape (e.g. `false`, `0`).
     */
    const explicitValueBound =
      valuePropName !== undefined &&
      hasExplicitValueBind(rawProps, valuePropName);
    const plainAuthoredEmpty =
      valuePropName !== undefined &&
      !explicitValueBound &&
      rawProps[valuePropName] === '' &&
      finalProps[valuePropName] === '';
    const formStateHasKey =
      formKey !== undefined &&
      Object.prototype.hasOwnProperty.call(dsm.formState, formKey);
    const autoBindsValue =
      formKey !== undefined &&
      valuePropName !== undefined &&
      !explicitValueBound &&
      (formStateHasKey || !(valuePropName in finalProps) || plainAuthoredEmpty);
    const boundFormValue =
      formStateHasKey && formKey !== undefined
        ? dsm.formState[formKey]
        : undefined;
    const authoredDefault = finalProps.defaultValue;
    const seedPending =
      seedAwaitsTemplatePaths &&
      !formStateHasKey &&
      hasMissingTemplatePath(rawProps.defaultValue, bindingContext);
    const defaultValueSeed =
      seedDefaultValue &&
      autoBindsValue &&
      !seedPending &&
      authoredInputType !== 'password' &&
      typeof authoredDefault === 'string' &&
      authoredDefault !== ''
        ? authoredDefault
        : undefined;
    if (autoBindsValue && valuePropName !== undefined) {
      if (boundFormValue === undefined && defaultValueSeed !== undefined) {
        finalProps[valuePropName] = defaultValueSeed;
      } else {
        finalProps[valuePropName] = formStateValueTransform
          ? formStateValueTransform(boundFormValue)
          : boundFormValue;
      }
      if (
        (boundFormValue === '' && defaultValueSeed !== undefined) ||
        seedPending
      ) {
        delete finalProps.defaultValue;
      }
      const shownValue = finalProps[valuePropName];
      if (leadFieldFormat && typeof shownValue === 'string') {
        finalProps[valuePropName] = leadFieldFormat(shownValue);
      }
      if (
        omitDefaultValueWhenBound &&
        finalProps[valuePropName] !== undefined
      ) {
        delete finalProps.defaultValue;
      }
    }

    /**
     * Controlled-state backing (ADR Seam 1 / F1). For each declared
     * value/change tuple, back the half the author did NOT bind with the
     * local `useState` captured above, deciding authored-vs-inject
     * independently PER HALF:
     *
     *   - value half unbound  → inject the local state value.
     *   - value half bound    → leave the author's resolved value.
     *   - change half unbound → inject a setter that updates local state.
     *   - change half bound   → compose: the injected setter updates
     *     local state AND the author's handler still runs (don't
     *     clobber). A setter is injected even when only the value is
     *     author-bound, so the control is not stuck.
     *
     * `onExpandedChange` / `onModalOpenChange` are NOT in
     * `DEFAULT_EVENT_PROP_NAMES`, so they are wired here rather than
     * through the generic `composeEventHandlers` path. The effective
     * authored change handler is read from `finalProps[changeProp]`,
     * which already reflects the static/resolved/`_action` precedence
     * from the assembly above.
     *
     * The injected setters/composed handlers are intentionally fresh
     * closures per render (no `useCallback`), consistent with this file's
     * deliberate no-memo stance — Puck rebuilds `rawProps` every render, so
     * referential stability would buy nothing. Consumers should not memoize
     * on these controlled-change props.
     */
    for (let i = 0; i < controlledState.length; i++) {
      const { valueProp, changeProp } = controlledState[i];
      const [stateValue, setStateValue] = controlledStates[i];

      // Author-bound when an explicit `_bind.<valueProp>` was declared, the
      // prop resolved into `props`, or a `staticProps` value exists. We do
      // NOT test `valueProp in finalProps`: beyond `props`/`staticProps`
      // (already covered), `finalProps` only adds structural keys like
      // `loading`, so that check could only ever false-positive on a
      // descriptor whose `valueProp` collides with one of them.
      const valueAuthorBound =
        `${BIND_PREFIX}${valueProp}` in rawProps ||
        valueProp in props ||
        staticProps?.[valueProp] !== undefined;
      if (!valueAuthorBound) {
        finalProps[valueProp] = stateValue;
      }

      // The effective authored change handler after assembly reflects
      // static / resolved / `_action` precedence already folded into
      // `finalProps` above.
      const authoredChange =
        typeof finalProps[changeProp] === 'function'
          ? (finalProps[changeProp] as (...handlerArgs: unknown[]) => void)
          : undefined;
      const changeAuthorSupplied =
        `${ACTION_PREFIX}${changeProp}` in rawProps ||
        typeof rawProps[changeProp] === 'function' ||
        typeof staticProps?.[changeProp] === 'function' ||
        changeProp in composedHandlers ||
        typeof props[changeProp] === 'function';

      if (changeAuthorSupplied && valueAuthorBound) {
        // Both halves author-bound → the author fully owns the tuple;
        // inject nothing and leave `finalProps[changeProp]` as authored.
        // Note: if the author declared a change binding (e.g.
        // `_action.<changeProp>`) that resolves to no usable handler while
        // also binding the value, the control is intentionally left inert —
        // the author owns the tuple, so we do not silently reintroduce a
        // setter behind their back.
      } else if (changeAuthorSupplied && authoredChange) {
        // Value is driven by local state and the author supplied a
        // change handler → compose so local state tracks the change AND
        // the author's side-effect still runs (don't clobber). Forward
        // every arg to the authored handler and use the first as the next
        // state value, so callbacks invoked with extra args (e.g.
        // `(value, event)`) are not truncated.
        finalProps[changeProp] = (...args: unknown[]): void => {
          setStateValue(args[0]);
          authoredChange(...args);
        };
      } else {
        // Change half unbound (or bound to no usable handler) → inject a
        // plain setter so the strictly-controlled component can update
        // and does not trip React's "value without onChange" warning,
        // even when only the value half is author-bound. Uses the first
        // arg as the next value (mirrors the compose branch).
        finalProps[changeProp] = (...args: unknown[]): void => {
          setStateValue(args[0]);
        };
      }
    }

    /**
     * Single-option auto-select (opt-in via
     * `ComponentBindingMeta.autoSelectSingleOption`).
     *
     * When a form-bound dropdown resolves to exactly one selectable
     * option, commit it so the user does not have to open a
     * single-choice control. This powers the cascading-select chain:
     * `degree: general` collapses category to a lone `general` option,
     * which auto-selects and refetches the subject source, which in
     * turn collapses to a lone `general` and auto-selects.
     *
     * Two derived primitives keep the effect deps stable (no per-render
     * churn from the fresh `options` array identity) AND let it re-run
     * exactly when it must:
     *   - `singleOptionValue`: the lone option's value, or `undefined`
     *     when any guard fails (not opted in, no formKey, loading,
     *     disabled, or `options` is not a length-1 `{ value }[]`).
     *     While the source is pending, `options` resolves to `[]`, so
     *     this is `undefined` and no stale auto-select fires.
     *   - `currentFormValue`: the committed value. Including it in the
     *     deps is what heals the transient empty state when the
     *     `_action.onSourceChange: formState.clear` cascade clears this
     *     key in the same settle — the clear flips `currentFormValue`,
     *     re-running the effect to re-apply the lone option.
     *
     * Ordering: this effect is declared AFTER `useOnSourceChange`
     * above, and both are passive (`useEffect`) effects, so on a shared
     * source-settle the clear dispatch is enqueued first and this
     * auto-select dispatch second — the reducer applies them in order
     * and the lone value wins. Do NOT convert this to
     * `useLayoutEffect`: that would run it before the passive clear and
     * let the clear win instead.
     */
    /**
     * Preselected-answer capture (`seedDefaultValue`). The render above
     * already shows the default while the key is absent; this writes it
     * into `formState[formKey]` once, on mount, so accepting the
     * preselected answer and continuing keeps it. A cleared (`''`) key is
     * left alone. Declared before single-option auto-select so a lone
     * resolved option still wins in the same effect flush.
     */
    const didDecideDefaultSeedRef = useRef(false);
    const didReconcileHostSeedRef = useRef(false);
    const retainedOptionsRef = useRef<unknown>(undefined);
    const singleOptionValue =
      autoSelectSingleOption &&
      formKey !== undefined &&
      !loading &&
      finalProps.disabled !== true &&
      isResolvedOptionsArray(finalProps.options) &&
      finalProps.options.length === 1
        ? String(finalProps.options[0].value)
        : undefined;
    const currentFormValue =
      formKey !== undefined ? dsm.formState[formKey] : undefined;
    const updateFormState = dsm.updateFormState;
    useEffect(() => {
      if (didDecideDefaultSeedRef.current) return;
      if (formKey === undefined) return;
      if (currentFormValue !== undefined) {
        didDecideDefaultSeedRef.current = true;
        return;
      }
      if (defaultValueSeed === undefined) return;
      didDecideDefaultSeedRef.current = true;
      updateFormState(formKey, defaultValueSeed);
    }, [defaultValueSeed, currentFormValue, formKey, updateFormState]);
    // An errored source also settles with `loading === false` and `options`
    // `[]`. That is not an empty catalog — reconciling it would wipe the
    // host seed and report a false unmatched token. A resolved empty list
    // still reconciles.
    const resolvedOptionKey =
      !loading &&
      dataSourceStatus !== 'error' &&
      isResolvedOptionsArray(finalProps.options)
        ? JSON.stringify(
            finalProps.options.map((option) => String(option.value)),
          )
        : undefined;
    useEffect(() => {
      if (didReconcileHostSeedRef.current) return;
      if (formKey === undefined) return;
      if (!hostSeededFormKeys.has(formKey)) {
        didReconcileHostSeedRef.current = true;
        return;
      }
      if (resolvedOptionKey === undefined) return;
      didReconcileHostSeedRef.current = true;
      const optionValues = JSON.parse(resolvedOptionKey) as string[];
      const decision = reconcileHostSeededOptions({
        value: currentFormValue,
        optionValues,
      });
      decision.dropped.forEach(() => {
        newRelicPageAction('deInitialFormStateUnmatched', { formKey });
      });
      if (decision.next !== undefined) {
        updateFormState(formKey, decision.next);
      }
    }, [
      formKey,
      hostSeededFormKeys,
      resolvedOptionKey,
      currentFormValue,
      updateFormState,
    ]);
    useEffect(() => {
      if (singleOptionValue === undefined || formKey === undefined) return;
      if (currentFormValue === singleOptionValue) return;
      updateFormState(formKey, singleOptionValue);
    }, [singleOptionValue, currentFormValue, formKey, updateFormState]);

    /**
     * First-option auto-select (authored opt-in via
     * {@link AUTO_SELECT_FIRST_OPTION_PROP}).
     *
     * A picker whose dependent charts read its `formKey` renders those charts
     * empty until someone touches it. Committing the first option once the list
     * arrives is what makes the page useful on first paint.
     *
     * Only fires into an EMPTY key, so it seeds rather than overrides: a
     * selection restored from a query param, seeded into `initialFormState`, or
     * made by the user is never replaced when the option list settles again.
     * A cleared key is empty, so the cascade re-seeds it — the same healing
     * behaviour the single-option effect has, and for the same reason.
     *
     * Unlike the single-option case this does not disable anything. There are
     * other options; the author asked for a starting point, not a lock.
     */
    const firstOptionValue =
      autoSelectFirstOption &&
      formKey !== undefined &&
      !loading &&
      isResolvedOptionsArray(finalProps.options) &&
      finalProps.options.length > 0
        ? String(finalProps.options[0].value)
        : undefined;
    /**
     * Written as the plain string the option carries rather than as a
     * one-entry list. The read side goes through the control's own
     * `formStateValueTransform`, which reads a string as a one-entry list, so a
     * multi-value control shows the seeded pill either way -- and the string
     * form is the one a single-value control can also read.
     */
    const formValueIsEmpty =
      formStateValueToKeys(currentFormValue).length === 0;
    useEffect(() => {
      if (firstOptionValue === undefined || formKey === undefined) return;
      if (!formValueIsEmpty) return;
      updateFormState(formKey, firstOptionValue);
    }, [firstOptionValue, formValueIsEmpty, formKey, updateFormState]);

    /**
     * Single-option auto-disable (same `autoSelectSingleOption` opt-in).
     *
     * A dropdown whose resolved options have collapsed to exactly one
     * selectable entry is not a meaningful choice — the lone value is
     * auto-committed by the effect above — so disable the control to
     * signal there is nothing to pick. When a parent change repopulates
     * it with more than one option (or it has none yet, while the source
     * is pending), the force is lifted and the component's own
     * `disabled` (author value, default enabled) applies again.
     *
     * Gated on `singleOptionValue !== undefined`, i.e. the exact same
     * conditions that commit the lone value: opted in, form-bound
     * (`formKey` present), not `loading`, not author-`disabled`, and a
     * resolved single-option list. This keeps the lock aligned with the
     * `autoSelectSingleOption` contract (form-bound only) so a select
     * without a `formKey` — or one the author already disabled — is
     * never force-locked, and a control can never end up disabled but
     * unselected. The force is written AFTER the auto-select guard so it
     * cannot suppress the commit.
     */
    if (singleOptionValue !== undefined) {
      finalProps.disabled = true;
    }

    /**
     * Untrusted rich-HTML sink (SAD §15.3). For each prop the registry
     * designates as HTML-bearing (e.g. `RankingCard.children`, the CMS
     * blurb), replace a resolved non-empty string with sanitized React
     * nodes via the shared `RichText` / `parseHtmlString` allowlist sink
     * (strips `<script>`, `javascript:`/unsafe URI schemes, inline
     * `on*=` handlers — mirroring the dynamic-flows publish-time
     * `xss_prevention` rule as render-time defense-in-depth).
     *
     * Non-string, empty, or whitespace-only values pass through
     * untouched, so an absent blurb or an empty slot array renders
     * exactly as today. This is a plain, fixed pass over `finalProps`
     * (no hooks), so Rules-of-Hooks safety is preserved.
     *
     * The render `variant` comes from the descriptor (surfaced per
     * component/prop in the registry — e.g. the ranking-list card blurb
     * slot is `b5`), so the enhancer imposes no typography of its own;
     * an omitted `variant` falls through to RichText's own default.
     */
    for (const { prop, variant } of htmlProps) {
      const value = finalProps[prop];
      if (typeof value === 'string' && value.trim().length > 0) {
        finalProps[prop] = <RichText html={value} variant={variant} />;
      }
    }

    const scrollPointName = readScrollPointName(
      resolvedMeta && typeof resolvedMeta === 'object'
        ? (resolvedMeta as Record<string, unknown>)
        : undefined,
    );
    const scrollPointProps = scrollPointName
      ? { [SCROLL_POINT_ATTRIBUTE]: scrollPointName }
      : undefined;

    /**
     * `stableKey` is composed above so it can also pin the
     * `useOnSourceChange` settled-snapshot store. Composition,
     * skip conditions, and segment semantics:
     *
     * Composition:
     *   `<flowSessionId>::<dslConfigId>::<stepIndex>::<puckNodeId>[::<itemIndexPath>]`
     *
     * Each segment is independently meaningful:
     *   - `puckNodeId` (`rawProps.id`): identifies the DSL node within
     *     the saved Puck data tree. Stable across renders of the same
     *     data, distinct between sibling components in the same step.
     *   - `stepIndex`: scopes the key to a single step render so a
     *     user navigating forward and back to the same step within
     *     one session sees a fresh fire opportunity (matches the
     *     standard render path's per-step ProductViewed semantics).
     *   - `dslConfigId`: scopes the key to a single DSL config
     *     instance — multiple distinct DSL configs in one tab don't
     *     cross-contaminate.
     *   - `flowSessionId`: scopes the key to a single provider-mount
     *     session, so a fresh mount of the same flow in the same tab
     *     (SPA nav, widget reopen, parent re-key) does not silently
     *     inherit dedup state from a prior session.
     *   - `itemIndexPath`: the enclosing repeater row's
     *     identity path (`useItemScopeIndexPath`), appended ONLY when
     *     this node renders inside a `platform.dataRepeater`. A
     *     repeater renders the same `itemSlot` template — and thus the
     *     same `puckNodeId` — once per row, so without this segment
     *     every repeated card (e.g. each result card in a "More
     *     matches" list) shares one `stableKey` and all but the first
     *     are dropped by the fire-once dedup. The path keeps each row
     *     distinct (and stays unique under nested repeaters).
     *
     * Skipped (`stableKey={undefined}`) when:
     *   - No `<TrackingProvider>` is mounted (legacy / test contexts)
     *     — `Trackable` already short-circuits to its placeholder
     *     branch and never fires; `useOnSourceChange` falls back to a
     *     per-instance `useRef` (legacy behaviour preserved).
     *   - The Puck node has no `id` prop (host invoked the bound
     *     component outside the `<Render>` walker, e.g. ad-hoc
     *     unit tests). The legacy WeakSet-on-DOM dedup remains the
     *     defense for that case.
     */
    const frameCopy = resolveFrameCopy(props, dsm.formState);
    const drawnRows = transformed ? transformed.rows : props[ADAPTER_ROWS_PROP];
    const rowsSourceName = boundRowsSourceName(rawProps);
    const rowsSource = rowsSourceName ? dsm.sources[rowsSourceName] : undefined;
    const staleAfterMs =
      typeof props.staleAfterMs === 'number' &&
      Number.isFinite(props.staleAfterMs)
        ? props.staleAfterMs
        : undefined;
    const [freshnessEpoch, setFreshnessEpoch] = useState(0);
    useEffect(() => {
      if (!statusFrame) return undefined;
      const updatedAt = rowsSource?.lastUpdatedAt;
      if (staleAfterMs === undefined || updatedAt === undefined) {
        return undefined;
      }
      const remaining = updatedAt + staleAfterMs - Date.now();
      // Equality is still current. Firing on the boundary can rerender in
      // that same millisecond and then never schedule another update.
      if (remaining < 0) return undefined;
      const timer = window.setTimeout(() => {
        setFreshnessEpoch((epoch) => epoch + 1);
      }, remaining + 1);
      return () => window.clearTimeout(timer);
    }, [statusFrame, rowsSource?.lastUpdatedAt, staleAfterMs, freshnessEpoch]);
    const frameStatus = statusFrame
      ? resolveBoundStatus({
          status: dataSourceStatus,
          bound: adapterApplies,
          narrowedToNothing:
            !transformFailure &&
            dataSourceStatus === 'resolved' &&
            Array.isArray(drawnRows) &&
            drawnRows.length === 0,
          adapterError: transformFailure ?? adapterOutcome?.failure ?? null,
          lastUpdatedAt: rowsSource?.lastUpdatedAt,
          staleAfterMs,
          now: Date.now(),
        })
      : undefined;
    const heldOptions = optionsWhileLoading(
      componentName,
      keepOptionsWhileLoading,
      optionsSourcePending(rawProps, dsm.sources),
      finalProps.options,
      retainedOptionsRef.current,
    );
    finalProps.options = heldOptions.options;
    useEffect(() => {
      retainedOptionsRef.current = heldOptions.retain;
    }, [heldOptions.retain]);
    const childProps = frameStatus ? omitFrameProps(finalProps) : finalProps;
    const fillsContainer =
      frameStatus !== undefined && childProps.fillContainer === true;
    const controlProps = (
      scrollPointProps ? { ...childProps, ...scrollPointProps } : childProps
    ) as P;
    const control = holdsFieldCheckUntilRevealed(componentName, rawProps) ? (
      <FieldCheckErrorHold component={Component} props={controlProps} />
    ) : (
      <Component {...controlProps} />
    );
    const renderedNode = (
      <Trackable
        meta={trackingMeta}
        onClick={trackingWrapperClick}
        stableKey={stableKey}
        productContext={productContext}
        fieldValue={fieldViewValue}
        fieldViewReady={fieldViewReady}
        elementContext={componentProps}
        blockId={puckNodeId}
        dedupNodeId={dedupNodeId}
        bindingScope={clickBindingScope}
      >
        {control}
      </Trackable>
    );
    const rendered = frameStatus ? (
      <ChartFrame
        surface={toChartSurface(frameStatus.surface)}
        title={frameCopy.title}
        subtitle={frameCopy.description}
        fillContainer={fillsContainer}
        className={fillsContainer ? 'h-full' : undefined}
        emptyTitle={EMPTY_FRAME_TITLE}
        queryErrorMessage={
          frameStatus.errorSource === 'adapter'
            ? ADAPTER_ERROR_MESSAGE
            : undefined
        }
        isStale={frameStatus.isStale === true}
        staleMessage={STALE_FRAME_MESSAGE}
        staleBannerKey={rowsSource?.lastUpdatedAt}
        minHeightClassName={
          Component.displayName === 'KPIBanner' ? 'min-h-0' : undefined
        }
      >
        {frameStatus.surface === 'ready' ? renderedNode : null}
      </ChartFrame>
    ) : (
      renderedNode
    );
    const withScrollPoint = scrollPointName ? (
      <ScrollPointFallback name={scrollPointName}>
        {rendered}
      </ScrollPointFallback>
    ) : (
      rendered
    );
    if (!scopeOverlay) {
      return withScrollPoint;
    }
    return (
      <PublishedScopeProvider scope={scopeOverlay}>
        {withScrollPoint}
      </PublishedScopeProvider>
    );
  };

  BoundComponent.displayName = `Bound(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;

  /**
   * Outer visibility gate (ADR Seam 5) — the
   * hook-safe outer/inner split.
   *
   * This component runs a FIXED, unconditional minimal hook set
   * (`useDataSourceManager`, `useItemScope`,
   * `useActiveVisibilityBreakpoints`) so React's Rules of Hooks hold
   * across every render regardless of the mount decision. The breakpoint
   * hook is always called but subscribed only when `showOn` is present.
   * It reads the raw `_meta.visibility` bag and:
   *   - `when` false → `null` (mount-only; ignores `mode`)
   *   - `showOn` miss + `mode: 'mount'` / absent → `null`
   *   - `showOn` miss + `mode: 'css'` → keep mounted, CSS-hide
   *   - match → render the inner {@link BoundComponent}
   *
   * A TRUE unmount skips `<Trackable>`, the hook-heavy body, and
   * `useOnSourceChange` — no phantom `ProductViewed`/`FieldViewed`.
   * The node's `_dataSource` stays registered here, outside that
   * decision, so a `when` predicate can read it as `self`.
   * `mode: 'css'` intentionally leaves Trackable mounted; prefer
   * `mode: 'mount'` for tracked nodes.
   *
   * `useItemScope()` resolves the current `platform.dataRepeater` row
   * (the repeater renders each row's slot template inside
   * `ItemScopeProvider`), so `when: { source: 'item', … }` evaluates per
   * row automatically — no repeater changes needed.
   */
  const VisibilityGate: React.FC<Record<string, unknown>> = (rawProps) => {
    const dsm = useDataSourceManager();
    const item = useItemScope();
    const ancestorScope = usePublishedScope();
    const nodeSource = readNodeSource(rawProps, ancestorScope);
    const nodeDataSourceRef = useRef(nodeSource.declaration);
    nodeDataSourceRef.current = nodeSource.declaration;
    const nodeScopeRef = useRef(nodeSource.scope);
    nodeScopeRef.current = nodeSource.scope;
    useEffect(() => {
      if (!nodeSource.id) return;
      const register = dsm.registerNodeSource;
      const unregister = dsm.unregisterNodeSource;
      if (!register || !unregister) {
        warnOnce(
          'node-data-source',
          '[data-source] This data source manager cannot register a node _dataSource; the node renders without it.',
        );
        return;
      }
      const declaration = nodeDataSourceRef.current;
      if (!declaration) return;
      register(nodeSource.id, declaration, nodeScopeRef.current ?? {});
      return () => {
        unregister(nodeSource.id);
      };
    }, [nodeSource.id, dsm.registerNodeSource, dsm.unregisterNodeSource]);
    const rawVisibility = rawProps[VISIBILITY_META_KEY];
    // Always call the hook (Rules of Hooks). Skip subscribe/re-render
    // when there is no non-empty `showOn` — `when`-only and no-meta
    // nodes never need breakpoint updates.
    const needsBreakpoints = rawVisibilityNeedsShowOn(rawVisibility);
    const activeBreakpoints = useActiveVisibilityBreakpoints(needsBreakpoints);

    if (rawVisibility === undefined) {
      return <BoundComponent {...rawProps} />;
    }

    const parsed = parseVisibilityMeta(rawVisibility);
    if (parsed.ok === false) {
      newRelicNoticeError(
        new Error(
          `[binding-enhancer] Malformed _meta.visibility: ${parsed.reason}`,
        ),
        { source: 'binding-enhancer:visibility' },
      );
      return <BoundComponent {...rawProps} />;
    }

    const { meta } = parsed;
    const ctx = {
      sources: nodeSource.id
        ? withSelfSource(dsm.sources, nodeSource.id)
        : dsm.sources,
      formState: dsm.formState,
      flowContext: dsm.flowContext,
      item,
    };

    if (meta.when !== undefined && !evaluateVisibilityWhen(meta.when, ctx)) {
      return null;
    }

    const showOnMatches = evaluateShowOn(meta.showOn, activeBreakpoints);

    // CSS mode: always mount when showOn is authored; pure media classes
    // hide off-breakpoint without a JS/hydration flash.
    if (
      meta.mode === 'css' &&
      meta.showOn !== undefined &&
      meta.showOn.length > 0
    ) {
      return (
        <CssBreakpointVisibility showOn={meta.showOn}>
          <BoundComponent {...rawProps} />
        </CssBreakpointVisibility>
      );
    }

    if (!showOnMatches) {
      return null;
    }
    return <BoundComponent {...rawProps} />;
  };

  VisibilityGate.displayName = `VisibilityGate(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;

  // Stamp the OUTER component so `applyBindings` still recognises a
  // wrapped component and skips double-wrapping (idempotency).
  (VisibilityGate as unknown as { [BOUND_RENDER_FLAG]: boolean })[
    BOUND_RENDER_FLAG
  ] = true;
  Object.defineProperty(VisibilityGate, 'authoredDslType', {
    configurable: true,
    enumerable: true,
    get() {
      return authoredBlockType.current;
    },
    set(value: unknown) {
      authoredBlockType.current =
        typeof value === 'string' && value.length > 0 ? value : undefined;
    },
  });

  return VisibilityGate as React.ComponentType<Record<string, unknown>>;
}

/**
 * Merge a per-component-type tracking default (from the
 * `componentTrackingDefaults` registry, keyed on hen-ui type name) with
 * the resolved `_meta.*` map collected by the binding resolver.
 *
 * Resolution order — author overrides always win:
 *   1. `trackingDefault` provides the baseline `kind`, `fieldType`, etc.
 *      for the wrapped component type (e.g. every `Select` is a `field`).
 *   2. Author-supplied `_meta.tracking.*` keys layer on top, overriding
 *      individual fields without having to restate the whole slice.
 *   3. For `kind === 'field'`, when neither author nor default supplied
 *      `fieldName` / `leadFieldAssociated` and the DSL node declared a
 *      `formKey`, the formKey is used as the fallback. Mirrors the
 *      `FieldInputted` dispatcher in `composeEventHandlers` so
 *      `FieldViewed` (fired by `Trackable`) and `FieldInputted` (fired
 *      here) carry matching `fieldName` / `fieldId` for the same
 *      logical node — the `Trackable.fireViewEvent`'s `'field'` branch
 *      reads these directly from the merged slice. Without this
 *      fallback, `FieldViewed` would emit `fieldName: undefined` while
 *      `FieldInputted` emits the formKey, producing nonsense for
 *      analytics consumers correlating the two events.
 *
 * Returns the same `resolvedMeta` reference when no default is supplied
 * AND the resolver collected nothing — preserves the legacy "no meta,
 * no Trackable wrapper observable" path verbatim. When a default exists
 * but the author authored no other `_meta.*` keys, we still emit a
 * tracking-only meta so the `<Trackable>` wrapper switches to its
 * active branch (`data-edu-trackable="active"`) and mounts the
 * IntersectionObserver — which is the entire point of the descriptor
 * registry.
 */
function pathSegmentsFromDotPath(path: string): ReadonlyArray<PathSegment> {
  return path
    .split('.')
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .map((name) => ({ type: 'key' as const, name }));
}

function readStringPropPath(
  props: Readonly<Record<string, unknown>>,
  path: string,
): string | undefined {
  const value = getByPath(props, pathSegmentsFromDotPath(path));
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * When a catalog entry declares {@link ComponentBindingMeta.outboundUrlPropPath},
 * copy the resolved prop onto the merged tracking slice so `ProductLoaded` /
 * `ProductViewed` / `ProductClicked` carry the CTA destination without
 * duplicating the URL in `_meta.tracking`. Author overrides win.
 */
function enrichTrackingOutboundUrl(
  trackingMeta: Readonly<Record<string, unknown>>,
  outboundUrl: string | undefined,
): Readonly<Record<string, unknown>> {
  if (!outboundUrl) return trackingMeta;
  const slice = trackingMeta.tracking;
  if (!isPlainObject(slice) || slice.kind !== 'product') return trackingMeta;
  if (typeof slice.outboundUrl === 'string' && slice.outboundUrl.length > 0) {
    return trackingMeta;
  }
  return {
    ...trackingMeta,
    tracking: { ...slice, outboundUrl },
  };
}

// Suffix a product card's base `location` with the repeater section (e.g.
// `dexp-results-page` → `dexp-results-page-recommended`) so sibling repeaters off
// one data source (`recommended.data` / `exact.data` / `related.data`) are
// distinguishable on product events. Section comes from `useItemScopeSection()`.
// An authored `_meta.tracking.location` always wins; cards with no base location
// (EL/widget) are left to fall through to the Monarch slot. Suffixing keeps the
// `dexp-results-page` prefix, so `LIKE` filters match but `=` filters need widening.
function applySectionLocation(
  trackingMeta: Readonly<Record<string, unknown>>,
  section: string | undefined,
  authoredLocation: string | undefined,
): Readonly<Record<string, unknown>> {
  if (!section) return trackingMeta;
  // Author per-instance location always wins.
  if (authoredLocation !== undefined) return trackingMeta;
  const slice = trackingMeta.tracking;
  if (!isPlainObject(slice) || slice.kind !== 'product') return trackingMeta;
  // Only refine a base location the card already carries; EL/widget nodes carry
  // none and must keep falling through to the Monarch slot.
  const base =
    typeof slice.location === 'string' && slice.location.length > 0
      ? slice.location
      : undefined;
  if (!base) return trackingMeta;
  return {
    ...trackingMeta,
    tracking: { ...slice, location: `${base}-${section}` },
  };
}

/**
 * The scroll point is stamped on the block, not tracked. Left in, it would
 * make `Trackable` add a placeholder wrapper, and a wrapper breaks parents
 * that style their direct children (Split columns).
 */
function omitScrollPoint(
  resolvedMeta: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  if (!('scrollPoint' in resolvedMeta)) return resolvedMeta;
  const rest: Record<string, unknown> = { ...resolvedMeta };
  delete rest.scrollPoint;
  return rest;
}

function mergeTrackingMeta(
  trackingDefault: TrackingMeta | undefined,
  resolvedMeta: Readonly<Record<string, unknown>>,
  formKey: string | undefined,
): Readonly<Record<string, unknown>> {
  const authoredTracking = readAuthoredTrackingSlice(resolvedMeta);

  if (!trackingDefault) {
    return isProductTrackingDisabled(authoredTracking) &&
      !hasLifecycleBinding(authoredTracking)
      ? omitTrackingSlice(resolvedMeta)
      : resolvedMeta;
  }

  // A shallow spread: an authored `customDimensions` map, even `{}`,
  // replaces the default map rather than merging into it.
  const merged: Record<string, unknown> = {
    ...trackingDefault,
    ...(authoredTracking ?? {}),
  };

  // formKey fallback for field nodes — only fires when neither
  // author nor default declared the key. Gated on `kind === 'field'`
  // so element / product / submit nodes don't accidentally pick up
  // a `fieldName` they have no semantic for.
  if (merged.kind === 'field' && formKey) {
    if (merged.fieldName === undefined) merged.fieldName = formKey;
    if (merged.leadFieldAssociated === undefined) {
      merged.leadFieldAssociated = formKey;
    }
  }

  if (isProductTrackingDisabled(merged) && !hasLifecycleBinding(merged)) {
    return omitTrackingSlice(resolvedMeta);
  }

  return {
    ...resolvedMeta,
    tracking: merged,
  };
}

/**
 * Read the authored `_meta.tracking` object from the resolved meta map.
 *
 * @param {Readonly<Record<string, unknown>>} resolvedMeta - Bound `_meta.*` map.
 * @returns {Readonly<Record<string, unknown>> | undefined} The tracking object, if present.
 */
function readAuthoredTrackingSlice(
  resolvedMeta: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> | undefined {
  const authored = resolvedMeta.tracking;
  if (
    authored !== undefined &&
    authored !== null &&
    typeof authored === 'object' &&
    !Array.isArray(authored)
  ) {
    return authored as Readonly<Record<string, unknown>>;
  }
  return undefined;
}

/**
 * Drop the merged tracking slice so Product-off does not default to Element.
 *
 * @param {Readonly<Record<string, unknown>>} resolvedMeta - Bound `_meta.*` map.
 * @returns {Readonly<Record<string, unknown>>} The same map without `tracking`.
 */
function omitTrackingSlice(
  resolvedMeta: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  if (!('tracking' in resolvedMeta)) return resolvedMeta;
  const rest = { ...resolvedMeta };
  delete rest.tracking;
  return rest;
}

/**
 * True when the saved node declares `_bind.<prop>` (flat canonical key
 * or the editor `_bind` map). A static prop of the same name is not a
 * bind — catalog defaults such as `selectedValues: []` land there.
 */
function hasExplicitValueBind(
  rawProps: Readonly<Record<string, unknown>>,
  valuePropName: string,
): boolean {
  const flat = rawProps[`${BIND_PREFIX}${valuePropName}`];
  if (flat != null && flat !== '') return true;
  const bindMap = rawProps._bind;
  if (!isPlainObject(bindMap)) return false;
  const nested = bindMap[valuePropName];
  return nested != null && nested !== '';
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function isPublishedScopeBag(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value) || React.isValidElement(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function readNodeSource(
  rawProps: Record<string, unknown>,
  ancestorScope: Readonly<Record<string, unknown>>,
): {
  readonly declaration?: ReportingModelDataSourceDeclaration;
  readonly scope?: Readonly<Record<string, unknown>>;
  readonly id: string;
} {
  const { declaration } = splitNodeDataSource(rawProps);
  if (!declaration) return { id: '' };
  const scope = scopeReadBy(declaration, ancestorScope);
  return {
    declaration,
    scope,
    id: `node:${stableStringify(declaration)}\u0000${scopeContentSignature(scope)}`,
  };
}

/** Binding root through which a node reads its own `_dataSource`. */
const SELF_SOURCE = 'self';

const UNREGISTERED_SELF: ResolvedDataSource = {
  data: undefined,
  status: 'pending',
};

/**
 * Sources as one node sees them: its own `_dataSource` under `self`
 * (pending until registered). A declared source named `self` is hidden
 * inside that node.
 */
function withSelfSource(
  sources: Readonly<Record<string, ResolvedDataSource>>,
  nodeSourceId: string,
): Readonly<Record<string, ResolvedDataSource>> {
  if (Object.prototype.hasOwnProperty.call(sources, SELF_SOURCE)) {
    warnOnce(
      'node-data-source',
      `[data-source] A data source named "${SELF_SOURCE}" is shadowed by the node's own _dataSource inside that node.`,
    );
  }
  return {
    ...sources,
    [SELF_SOURCE]: sources[nodeSourceId] ?? UNREGISTERED_SELF,
  };
}

/**
 * `_dataSource` is a fetch instruction, not a component prop. It is read
 * from the raw node (templates stay intact) and registered against the
 * ancestor published scope. A value that is not one reportingModel
 * declaration is dropped.
 */
function splitNodeDataSource(props: Record<string, unknown>): {
  componentProps: Record<string, unknown>;
  declaration: ReportingModelDataSourceDeclaration | undefined;
} {
  if (!Object.prototype.hasOwnProperty.call(props, '_dataSource')) {
    return { componentProps: props, declaration: undefined };
  }
  const { _dataSource, ...componentProps } = props;
  if (!isReportingModelDeclaration(_dataSource)) {
    warnOnce(
      'node-data-source',
      '[data-source] _dataSource must be a reportingModel declaration.',
    );
    return { componentProps, declaration: undefined };
  }
  return { componentProps, declaration: _dataSource };
}

function isReportingModelDeclaration(
  value: unknown,
): value is ReportingModelDataSourceDeclaration {
  if (!isPlainObject(value)) return false;
  if (value.kind !== 'reportingModel') return false;
  if (typeof value.modelId !== 'string' || value.modelId.length === 0) {
    return false;
  }
  return Array.isArray(value.metrics);
}

/**
 * `_scope` is a publish instruction, not a component prop. Interpolated
 * values (already resolved onto `props`) wrap the element. Anything else
 * is dropped so the component never sees the key.
 */
function splitPublishedScope(props: Record<string, unknown>): {
  componentProps: Record<string, unknown>;
  scopeOverlay: Record<string, unknown> | undefined;
} {
  if (!Object.prototype.hasOwnProperty.call(props, '_scope')) {
    return { componentProps: props, scopeOverlay: undefined };
  }
  const { _scope, ...componentProps } = props;
  if (!isPublishedScopeBag(_scope)) {
    warnOnce(
      'published-scope',
      '[published-scope] _scope must be a plain object.',
    );
    return { componentProps, scopeOverlay: undefined };
  }
  return { componentProps, scopeOverlay: _scope };
}

function readTrackingFieldMeta(
  trackingMeta: Readonly<Record<string, unknown>> | undefined,
  hostDefaults = false,
): FieldTrackingMeta | null {
  if (!trackingMeta) return null;
  const slice = trackingMeta.tracking;
  if (!isPlainObject(slice)) return null;
  if (slice.kind !== 'field') return null;
  if (hostDefaults && slice.enabled === false) return null;
  const out: FieldTrackingMeta = {};
  if (typeof slice.fieldName === 'string') out.fieldName = slice.fieldName;
  if (typeof slice.fieldType === 'string') out.fieldType = slice.fieldType;
  if (typeof slice.leadFieldAssociated === 'string') {
    out.leadFieldAssociated = slice.leadFieldAssociated;
  }
  return out;
}

/**
 * Field metadata to sanitize against. Of the tracking lead field, the
 * component's `_meta.leadFieldAssociated`, the tracking field name and the
 * formKey, the first one `isPii` matches is used, else the first one
 * present. Empty strings count as absent.
 */
function resolveSanitizeFieldMeta(
  fieldMeta: FieldTrackingMeta | null,
  resolvedMeta: Readonly<Record<string, unknown>> | undefined,
  formKey: string | undefined,
): FieldTrackingMeta | null {
  if (!fieldMeta) return null;
  const candidates = [
    fieldMeta.leadFieldAssociated,
    resolvedMeta?.leadFieldAssociated,
    fieldMeta.fieldName,
    formKey,
  ].filter(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate !== '',
  );
  const leadField =
    candidates.find((candidate) => isPii(candidate)) ?? candidates[0];
  if (leadField === undefined || leadField === fieldMeta.leadFieldAssociated) {
    return fieldMeta;
  }
  return { ...fieldMeta, leadFieldAssociated: leadField };
}

/**
 * Resolve the runtime `navigation.linkout` param fallbacks threaded into the
 * conversion redirect. Mirrors the `ProductClicked` payload EXACTLY (same
 * inputs + precedence — see the click-dispatch slot) so the conversion is
 * classified and attributed identically to the click:
 *
 *   - `formatType` / `formatSubtype` — the merged {@link ClickTrackingMeta}
 *     classification wins, otherwise the item-derived
 *     {@link ProductIdentityContext}. The two differ: `trackingClickMeta` folds
 *     the registry default + authored `_meta.tracking` (where Editorial
 *     Listings get `dexp-ad` / `dexp-editorial-listings`), while
 *     `productContext` is always the offering surface (`dexp-offering`, with
 *     `dexp-unqualified` for StudyMatch rows and `dexp-results` for bucketed
 *     results). Reading only the item-derived context mislabeled EL linkouts.
 *   - `correlationId` — the per-click correlation, using the SAME precedence
 *     `createProductClickedPayload` does: `productContext.productClickedCorrelationId`
 *     (the row's click id) else the flow-level `formViewedCorrelationId`. This
 *     is what ties the server-side `ConversionTracked` / `ConversionTrackedMeta`
 *     back to the `ProductClicked` (parity with the Voyager POST's top-level
 *     `conversion.correlationId`).
 *
 * Returns `undefined` when nothing resolves (non-product / non-result nodes)
 * so the resolver injects nothing and the BFF keeps its own defaults.
 */
function resolveLinkoutParamFallbacks(
  trackingClickMeta: ProductClickTrackingMeta | null,
  productContext: ProductIdentityContext | undefined,
  formViewedCorrelationId: string | undefined,
  formState: Readonly<Record<string, string>>,
):
  | {
      formatType?: string;
      formatSubtype?: string;
      correlationId?: string;
      country?: string;
      state?: string;
    }
  | undefined {
  const formatType =
    trackingClickMeta?.formatType ?? productContext?.formatType;
  const formatSubtype =
    trackingClickMeta?.formatSubtype ?? productContext?.formatSubtype;
  const correlationId =
    productContext?.productClickedCorrelationId || formViewedCorrelationId;
  const { country, state } = deriveGeoFromFormState(formState);
  if (
    formatType === undefined &&
    formatSubtype === undefined &&
    correlationId === undefined &&
    country === undefined &&
    state === undefined
  ) {
    return undefined;
  }
  return {
    ...(formatType !== undefined ? { formatType } : {}),
    ...(formatSubtype !== undefined ? { formatSubtype } : {}),
    ...(correlationId !== undefined ? { correlationId } : {}),
    ...(country !== undefined ? { country } : {}),
    ...(state !== undefined ? { state } : {}),
  };
}

/**
 * Derive location fallback params for `navigation.linkout` from the current
 * DSM form state. `geolocation` (`CC-SUB` or country-only `CC`) is preferred
 * because it is the same resolved region key used for offerings budget
 * matching. Falls back to `countryCode` only when geolocation is missing
 * or not a one- or two-segment value.
 */
function deriveGeoFromFormState(formState: Readonly<Record<string, string>>): {
  country?: string;
  state?: string;
} {
  const geolocation = formState.geolocation?.trim().toUpperCase();
  if (geolocation) {
    const parts = geolocation
      .split('-')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (parts.length === 2) {
      const [country, state] = parts;
      return { country, state };
    }
    if (parts.length === 1) {
      return { country: parts[0] };
    }
  }
  const country = formState.countryCode?.trim().toUpperCase();
  return country ? { country } : {};
}

/**
 * Subset of {@link TrackingMeta} the click dispatcher needs.
 * Surfaced separately from the merged `meta` shape so the dispatch
 * call site has a typed handle without re-walking the whole object.
 *
 * Returns `null` when the merged tracking slice is missing or the
 * declared `kind` is not `'product'` — in either case the click slot
 * should NOT receive the dispatch wrapper.
 *
 * `kind: 'submit'` is not recognised by this dispatcher. Submit CTAs
 * route their click through `_action.onClick` (e.g. `flow.forward`)
 * into `handleStepForward`; the provider-owned
 * `useForwardStepEventFiring` then fires the spec-compliant
 * `FormStarted` / `ProductClicked` / `FormContinued` triple per
 * Confluence "Dynamic Experience Eventing Requirements" §6.1 / §6.7.
 * A standalone `SubmitClicked` is never emitted.
 */
interface ProductClickTrackingMeta {
  kind: 'product';
  ctaText?: string;
  outboundUrl?: string;
  actionOutcome?: string;
  location?: string;
  formatType?: string;
  formatSubtype?: string;
  productName?: string;
  productVariant?: string;
}

interface ElementClickTrackingMeta {
  kind: 'element';
  enabled: true;
  elementType?: string;
  name?: string;
  htmlId?: string;
  location?: string;
  text?: string;
  fields?: Readonly<Record<string, unknown>>;
  customDimensions?: unknown;
}

type ClickTrackingMeta = ProductClickTrackingMeta | ElementClickTrackingMeta;

function readTrackingClickMeta(
  trackingMeta: Readonly<Record<string, unknown>> | undefined,
  policy?: TrackingHostPolicy,
): ClickTrackingMeta | null {
  if (!trackingMeta) return null;
  const slice = trackingMeta.tracking;
  if (!isPlainObject(slice)) return null;
  if (
    isDefaultOnHostPolicy(policy) &&
    slice.kind === 'element' &&
    slice.enabled !== false
  ) {
    const out: ElementClickTrackingMeta = { kind: 'element', enabled: true };
    if (typeof slice.elementType === 'string') {
      out.elementType = slice.elementType;
    }
    if (typeof slice.name === 'string') out.name = slice.name;
    if (typeof slice.htmlId === 'string') out.htmlId = slice.htmlId;
    if (typeof slice.location === 'string') out.location = slice.location;
    if (typeof slice.text === 'string') out.text = slice.text;
    if (isPlainObject(slice.fields)) out.fields = slice.fields;
    if (slice.customDimensions !== undefined) {
      out.customDimensions = slice.customDimensions;
    }
    return out;
  }
  if (
    policy === 'explicit-node-enable' &&
    slice.kind === 'element' &&
    slice.enabled === true
  ) {
    const out: ElementClickTrackingMeta = {
      kind: 'element',
      enabled: true,
    };
    if (typeof slice.elementType === 'string') {
      out.elementType = slice.elementType;
    }
    if (typeof slice.name === 'string') out.name = slice.name;
    if (typeof slice.htmlId === 'string') out.htmlId = slice.htmlId;
    if (typeof slice.location === 'string') out.location = slice.location;
    if (typeof slice.text === 'string') out.text = slice.text;
    if (isPlainObject(slice.fields)) out.fields = slice.fields;
    if (slice.customDimensions !== undefined) {
      out.customDimensions = slice.customDimensions;
    }
    return out;
  }
  if (policy === 'explicit-node-enable' || isDefaultOnHostPolicy(policy)) {
    return null;
  }
  if (isProductTrackingDisabled(slice)) return null;
  if (slice.kind !== 'product') return null;
  const out: ProductClickTrackingMeta = { kind: 'product' };
  if (typeof slice.ctaText === 'string') out.ctaText = slice.ctaText;
  if (typeof slice.outboundUrl === 'string')
    out.outboundUrl = slice.outboundUrl;
  if (typeof slice.actionOutcome === 'string') {
    out.actionOutcome = slice.actionOutcome;
  }
  if (typeof slice.location === 'string') out.location = slice.location;
  if (typeof slice.formatType === 'string') out.formatType = slice.formatType;
  if (typeof slice.formatSubtype === 'string') {
    out.formatSubtype = slice.formatSubtype;
  }
  if (typeof slice.productName === 'string')
    out.productName = slice.productName;
  if (typeof slice.productVariant === 'string') {
    out.productVariant = slice.productVariant;
  }
  return out;
}

interface ReadyClickBinding {
  readonly binding: EventBinding;
  readonly entry: CatalogEventEntry;
  readonly fields: Readonly<Record<string, BindingFieldSource>>;
  readonly customDimensions: Readonly<Record<string, BindingFieldSource>>;
}

function readyClickBindings(
  slice: unknown,
  acceptsClickHandler: (name: string) => boolean,
): ReadyClickBinding[] {
  const ready: ReadyClickBinding[] = [];
  for (const binding of readBindings(slice)) {
    if (!acceptsClickHandler(binding.on)) continue;
    const checked = checkBinding(binding, 'click', eventCatalog);
    if ('reason' in checked) {
      continue;
    }
    ready.push({
      binding,
      entry: checked.entry,
      fields: checked.fields,
      customDimensions: checked.customDimensions,
    });
  }
  return ready;
}

function bindingSourceData(
  sources: Readonly<Record<string, { readonly data?: unknown } | undefined>>,
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const key of Object.keys(sources)) {
    if (FORBIDDEN_KEYS.has(key)) continue;
    data[key] = sources[key]?.data;
  }
  return data;
}

interface ComposedClickSend {
  readonly bindingId: string;
  readonly name: string;
  readonly payload: Record<string, unknown>;
}

function composeReadyClick(
  ready: ReadyClickBinding,
  scope: BindingResolveScope,
  experience: BindingExperience,
  eventContext: EventContext,
): ComposedClickSend | undefined {
  const resolvedFields: Record<string, string | undefined> = {};
  for (const key of Object.keys(ready.fields)) {
    const source = ready.fields[key];
    if (source) resolvedFields[key] = resolveBindingField(source, scope);
  }
  const resolvedCustomDimensions: Record<string, string | undefined> = {};
  for (const key of Object.keys(ready.customDimensions)) {
    const source = ready.customDimensions[key];
    if (source) {
      resolvedCustomDimensions[key] = resolveBindingField(source, scope);
    }
  }
  const composed = composeConfiguredEvent({
    binding: ready.binding,
    entry: ready.entry,
    resolvedFields,
    resolvedCustomDimensions,
    experience,
    eventContext,
    valueSets: eventCatalog.valueSets,
  });
  if ('reason' in composed) return undefined;
  return {
    bindingId: ready.binding.id,
    name: composed.name,
    payload: composed.payload,
  };
}

type EventHandler = (...args: unknown[]) => void;

function leadFieldFormatter(
  leadField: unknown,
): ((value: string) => string) | undefined {
  return isLeadFieldName(leadField)
    ? LEAD_FIELD_PRESETS[leadField].format
    : undefined;
}

interface CaretField {
  readonly value: string;
  readonly selectionStart: number | null;
  readonly ownerDocument: Document;
  setSelectionRange(start: number, end: number): void;
}

// Duck-typed: `instanceof` fails for a field in another frame (Admin canvas).
function asCaretField(target: unknown): CaretField | undefined {
  const field = target as Partial<CaretField> | null | undefined;
  return field &&
    typeof field.setSelectionRange === 'function' &&
    typeof field.value === 'string' &&
    field.ownerDocument
    ? (field as CaretField)
    : undefined;
}

/** Put the caret back after the same digits once the re-formatted value renders. */
function restoreDigitCaret(
  event: unknown,
  typed: string,
  formatted: string,
): void {
  const field = asCaretField((event as { target?: unknown } | null)?.target);
  if (!field || typeof requestAnimationFrame !== 'function') return;
  const caret = field.selectionStart;
  if (caret === null || caret >= typed.length) return;
  let digits = typed.slice(0, caret).replace(/\D/g, '').length;
  let position = 0;
  while (position < formatted.length && digits > 0) {
    if (/\d/.test(formatted[position])) digits -= 1;
    position += 1;
  }
  // A forward Delete of a format character steps over it rather than sticking.
  const inputType = (event as { nativeEvent?: { inputType?: unknown } })
    .nativeEvent?.inputType;
  if (inputType === 'deleteContentForward') {
    while (position < formatted.length && !/\d/.test(formatted[position])) {
      position += 1;
    }
  }
  requestAnimationFrame(() => {
    if (field.ownerDocument.activeElement !== (field as unknown as Element)) {
      return;
    }
    if (field.value !== formatted) return;
    field.setSelectionRange(position, position);
  });
}

/**
 * Compose the final event-handler map for a bound component.
 *
 * Order of precedence inside a single event slot — compose **all**
 * callable sources, not just one authored source:
 *
 *   1. static handler from `meta.staticProps.<event>` — catalog
 *      wrapper default (lowest priority among authored, runs first
 *      so the wrapper's invariants apply before any DSL-supplied
 *      override).
 *   2. resolved/raw handler from `props.<event>` (a `_bind.<event>`
 *      that produced a function, or a raw passthrough handler from
 *      the host).
 *   3. resolved `_action.<event>` handler (DSL-declared analytics /
 *      navigation / etc.).
 *   4. `formKey` capture (when present, on the first available event
 *      prop name) — pushes the extracted value into DSM via
 *      `updateFormState`.
 *
 * All callable sources for a given event run, in that order, with the
 * same arguments. Each step is wrapped in a try/catch so a thrown
 * authored handler cannot prevent later sources from running.
 *
 * Only the slots that have at least one callable source emit a
 * composed handler — slots that are purely scaffolding stay absent so
 * React doesn't accidentally bind a no-op listener that masks a
 * default browser behaviour.
 */
function composeEventHandlers(args: {
  readonly eventPropNames: readonly string[];
  readonly staticProps: Readonly<Record<string, unknown>> | undefined;
  readonly resolvedProps: Readonly<Record<string, unknown>>;
  readonly actionHandlers: Readonly<Record<string, EventHandler>>;
  readonly formKey: string | undefined;
  readonly formKeyValueTransform: (value: unknown) => FormStateValue;
  /** Lead-field display format applied to a captured string. */
  readonly formatCapturedValue?: (value: string) => string;
  readonly updateFormState: (key: string, value: FormStateValue) => void;
  /**
   * Live tracking-context value (or `undefined` when no
   * `<TrackingProvider>` is mounted). When non-null AND
   * `fieldTrackingMeta` is non-null, the formKey capture wrapper
   * also dispatches `triggerFieldEvents('FieldInputted', …)` carrying
   * the captured value — the user's "field selected" event for the
   * DSL render path.
   */
  readonly tracking: TrackingContextValue | undefined;
  /**
   * Field metadata derived from the merged tracking slice. Non-null
   * only when `trackingMeta.tracking.kind === 'field'`. Pre-resolved
   * by the caller so this function stays free of meta-shape
   * inspection.
   */
  readonly fieldTrackingMeta: FieldTrackingMeta | null;
  /**
   * Field metadata the captured value is sanitized against. See
   * `resolveSanitizeFieldMeta`.
   */
  readonly sanitizeFieldMeta: FieldTrackingMeta | null;
  /**
   * Trailing-debounce scheduler for text-entry `FieldInputted`. Omitted
   * callers dispatch every capture immediately.
   */
  readonly scheduleFieldInput?: ScheduleFieldInput;
  readonly hostFieldSlice?: Readonly<Record<string, unknown>>;
  readonly hostFieldNodeId?: string;
  readonly hostFieldLabel?: string;
  /**
   * Authored component `type` prop. `'password'` redacts the wire
   * value even when the form key is not password-like.
   */
  readonly inputType?: string;
  /**
   * Name of the click event slot the binding enhancer queues for
   * product click dispatch. Defaults to `'onClick'` upstream when
   * the catalog metadata omits it.
   */
  readonly clickPropName: string;
  /**
   * Queue a tracking-only click under {@link TRACKING_WRAPPER_CLICK_SLOT}
   * for the `Trackable` wrapper instead of `clickPropName`. See
   * `ComponentBindingMeta`.
   */
  readonly trackingOnlyClickOnWrapper?: boolean;
  /**
   * Click-dispatch metadata derived from the merged tracking slice.
   * Non-null only when `trackingMeta.tracking.kind === 'product'`.
   * When non-null AND `tracking` is non-null AND the click slot
   * fires, the composed handler dispatches `triggerProductClickedEvent`
   * with the authored `ctaText` / `outboundUrl` / `actionOutcome` /
   * `location` plus StepCorrelation customDimensions.
   */
  readonly clickTrackingMeta: ClickTrackingMeta | null;
  /**
   * `kind` as authored on the tracking slice. The flow host leaves
   * `clickTrackingMeta` empty for `element`, so the suppress signal
   * reads this instead.
   */
  readonly declaredClickKind?: 'element' | 'product';
  readonly clickBindings: readonly ReadyClickBinding[];
  readonly clickDefaults: BindingDefaults;
  readonly clickBindingScope: BindingResolveScope;
  readonly stableKey?: string;
  /**
   * Per-card product identity (brand / presets / productId
   * / sku / position / correlation + format + location defaults) derived
   * from the enclosing repeater item by the caller for `kind: 'product'`
   * nodes. Threaded onto the `ProductClicked` dispatch so the click event
   * carries the same real product data as the `ProductLoaded` /
   * `ProductViewed` events (which receive it via `<Trackable>`). Without
   * this the `ProductClicked` payload shipped an empty product object
   * (`brand` / `name` / `variant` / `category` / `productId` / `sku`
   * blank, `formatType: 'dexp-entry-component'`). `undefined` for
   * non-result items so those payloads stay unchanged.
   */
  readonly productContext?: ProductIdentityContext;
  readonly fieldViewReady: boolean;
  // Results-card click `actionOutcome` default (`explore`/`application`):
  // overrides the inherited `dexp-question-flow` but stays below an authored
  // override. `undefined` for non-result product nodes.
  readonly resultActionOutcome?: string;
  /**
   * True when `_action.<clickPropName>` declares `navigation.advanceStep` /
   * `navigation.advanceStepWithoutSubmit` — i.e. this click is the one
   * `useHandleStepForward` will process. Only this click's product-click
   * decision may signal `tracking.entryProductClickSuppressedRef`; a
   * sibling control's click never reaches the forward-step firer.
   */
  readonly ownsForwardNavigation: boolean;
}): Record<string, EventHandler> {
  const {
    eventPropNames,
    staticProps,
    resolvedProps,
    actionHandlers,
    formKey,
    formKeyValueTransform,
    formatCapturedValue,
    updateFormState,
    tracking,
    fieldTrackingMeta,
    sanitizeFieldMeta,
    scheduleFieldInput,
    hostFieldSlice,
    hostFieldNodeId,
    hostFieldLabel,
    inputType,
    clickPropName,
    trackingOnlyClickOnWrapper,
    clickTrackingMeta,
    declaredClickKind,
    clickBindings,
    clickDefaults,
    clickBindingScope,
    stableKey,
    productContext,
    resultActionOutcome,
    fieldViewReady,
    ownsForwardNavigation,
  } = args;

  const formKeyTarget = formKey
    ? pickFormKeyTarget(eventPropNames, actionHandlers)
    : undefined;
  // The click dispatch target is only queued when the merged tracking
  // slice declares `kind: 'product'` AND a `<TrackingProvider>` is
  // mounted. Gating on `tracking` (not just on `clickTrackingMeta`)
  // means a `kind: 'product'` author who renders outside the
  // tracking surface still gets pass-through DOM behaviour without
  // an inert onClick wrapper.
  const clickSlotIsBound =
    actionHandlers[clickPropName] !== undefined ||
    formKeyTarget === clickPropName ||
    collectAuthoredHandlers(clickPropName, resolvedProps, staticProps).length >
      0;
  const clickTarget =
    tracking && clickTrackingMeta
      ? trackingOnlyClickOnWrapper && !clickSlotIsBound
        ? TRACKING_WRAPPER_CLICK_SLOT
        : clickPropName
      : undefined;

  // Deduplicate event-prop names while preserving insertion order. Built
  // as an array (not a `Set`) because the package compiles to ES5 without
  // `downlevelIteration`, which would silently turn `for…of <Set>` into
  // an array-style loop and skip every entry.
  const eventNames: string[] = [];
  const seen: Record<string, true> = {};
  const queue = (name: string): void => {
    if (!seen[name]) {
      seen[name] = true;
      eventNames.push(name);
    }
  };
  for (const name of Object.keys(actionHandlers)) {
    queue(name);
  }
  if (formKeyTarget) queue(formKeyTarget);
  if (clickTarget) queue(clickTarget);
  // A supported click handler follows the wrapper slot when the built-in
  // click does. `onChange`, `onSelect`, and unknown names such as
  // `children` are dropped before this queue, so they are not installed
  // as props. The wrapper decision does not depend on click metadata,
  // so `enabled: false` still delivers the binding.
  const useWrapperSlot =
    trackingOnlyClickOnWrapper === true && !clickSlotIsBound;
  const slotFor = (on: string): string =>
    useWrapperSlot && on === clickPropName ? TRACKING_WRAPPER_CLICK_SLOT : on;
  if (tracking) {
    for (const ready of clickBindings) {
      queue(slotFor(ready.binding.on));
    }
  }
  // Also queue any event slot where **both** an authored static
  // handler and an authored resolved/raw handler exist, so the
  // composed handler runs both. Slots with only a single authored
  // source and no `_action` / `formKey` target stay unqueued and
  // pass through `finalProps` unchanged (preserves referential
  // identity for hosts that rely on it).
  if (staticProps) {
    for (const name of Object.keys(staticProps)) {
      if (
        typeof staticProps[name] === 'function' &&
        typeof resolvedProps[name] === 'function'
      ) {
        queue(name);
      }
    }
  }

  const out: Record<string, EventHandler> = {};
  for (const name of eventNames) {
    const authored = collectAuthoredHandlers(name, resolvedProps, staticProps);
    const action = actionHandlers[name];
    const isFormKeySlot = formKey !== undefined && name === formKeyTarget;
    const isClickDispatchSlot =
      clickTarget !== undefined && name === clickTarget;
    let hasSlotBinding = false;
    for (const ready of clickBindings) {
      if (slotFor(ready.binding.on) === name) {
        hasSlotBinding = true;
        break;
      }
    }

    if (
      authored.length === 0 &&
      !action &&
      !isFormKeySlot &&
      !isClickDispatchSlot &&
      !hasSlotBinding
    ) {
      continue;
    }

    out[name] = (...handlerArgs: unknown[]): void => {
      for (const handler of authored) {
        try {
          handler.fn(...handlerArgs);
        } catch (err) {
          reportBoundRenderError(
            err,
            `binding-enhancer:authored:${name}:${handler.origin}`,
          );
        }
      }
      if (action) {
        try {
          action(...handlerArgs);
        } catch (err) {
          reportBoundRenderError(err, `binding-enhancer:_action:${name}`);
        }
      }
      if (isFormKeySlot && formKey) {
        const firstArg = handlerArgs.length > 0 ? handlerArgs[0] : undefined;
        const extractedValue = formKeyValueTransform(firstArg);
        const capturedValue =
          formatCapturedValue && typeof extractedValue === 'string'
            ? formatCapturedValue(extractedValue)
            : extractedValue;
        try {
          updateFormState(formKey, capturedValue);
        } catch (err) {
          reportBoundRenderError(
            err,
            `binding-enhancer:updateFormState:${formKey}`,
          );
        }
        if (
          typeof extractedValue === 'string' &&
          typeof capturedValue === 'string' &&
          capturedValue !== extractedValue
        ) {
          restoreDigitCaret(firstArg, extractedValue, capturedValue);
        }
        // When this slot is the formKey capture for a tracked field
        // AND a `<TrackingProvider>` is mounted, also dispatch
        // `FieldInputted` with the captured value. This is the
        // "field selected" wire event the legacy non-DSL flow
        // components emit via `useQuestionEvents.triggerFieldInputted`.
        // We dispatch from inside the formKey capture (not from
        // `Trackable`) because this is the only callsite that has the
        // user's value at the exact moment of selection. Field nodes
        // without a `formKey` declared cannot fire `FieldInputted` —
        // there's nothing to capture; `FieldViewed` from `Trackable`
        // still fires on visibility for those.
        if (tracking && fieldTrackingMeta) {
          try {
            const isDefaultOnHost = isDefaultOnHostPolicy(
              tracking.host?.policy,
            );
            if (isDefaultOnHost && !fieldViewReady) {
              markHostFieldViewConsumed(stableKey);
            }
            const eventValue = isDefaultOnHost
              ? normalizeHostFieldValue(capturedValue)
              : capturedValue;
            const sanitized = sanitizeFieldValue(
              eventValue,
              sanitizeFieldMeta ?? fieldTrackingMeta,
              formKey,
              tracking.eventContext?.currentStep as Step | undefined,
              inputType,
            );
            const merged = (
              isDefaultOnHost
                ? {
                    ...buildHostFieldEventContext({
                      page: tracking.host?.page,
                      tracking: hostFieldSlice,
                      formKey,
                      nodeId: hostFieldNodeId,
                      label: hostFieldLabel,
                      fieldValue: sanitized,
                    }),
                    correlationId: mintHostElementEventCorrelationId(),
                  }
                : {
                    ...(tracking.eventContext ?? {}),
                    ...buildStepCorrelationOverride(tracking),
                    fieldName: fieldTrackingMeta.fieldName ?? formKey,
                    fieldType: fieldTrackingMeta.fieldType,
                    fieldId: fieldTrackingMeta.leadFieldAssociated ?? formKey,
                    fieldValue: sanitized,
                  }
            ) as Parameters<typeof tracking.triggers.triggerFieldEvents>[1];
            const dispatchFieldInputted = (): void => {
              try {
                tracking.triggers.triggerFieldEvents('FieldInputted', merged);
              } catch (err) {
                reportBoundRenderError(
                  err,
                  `binding-enhancer:triggerFieldEvents:FieldInputted:${formKey}`,
                );
              }
            };
            if (
              scheduleFieldInput &&
              isTextEntryFieldType(fieldTrackingMeta.fieldType)
            ) {
              scheduleFieldInput(dispatchFieldInputted);
            } else {
              dispatchFieldInputted();
            }
          } catch (err) {
            reportBoundRenderError(
              err,
              `binding-enhancer:triggerFieldEvents:FieldInputted:${formKey}`,
            );
          }
        }
      }
      // Authored handlers, `_action`, and form state have already run.
      // The built-in click is next, then each configured binding in
      // array order. Linkout opens another tab, and an anchor follows
      // its href only after this handler returns, so a binding here
      // still calls `tagular` in the same turn.
      const slotBindings: ReadyClickBinding[] = [];
      for (const ready of clickBindings) {
        if (slotFor(ready.binding.on) === name) slotBindings.push(ready);
      }
      const suppressBuiltIn =
        clickDefaults[name] === 'suppress' ||
        (name === TRACKING_WRAPPER_CLICK_SLOT &&
          clickDefaults[clickPropName] === 'suppress');
      let builtInKind: 'element' | 'product' | undefined;
      let elementArgument:
        | Parameters<
            TrackingContextValue['triggers']['triggerElementClickedEvent']
          >[0]
        | undefined;
      let productArgument:
        | Parameters<
            TrackingContextValue['triggers']['triggerProductClickedEvent']
          >[0]
        | undefined;
      let composeContext: EventContext | undefined;
      if (
        isClickDispatchSlot &&
        tracking &&
        clickTrackingMeta &&
        (!suppressBuiltIn || slotBindings.length > 0)
      ) {
        try {
          if (clickTrackingMeta.kind === 'element') {
            const usesHostElementPayload =
              isDefaultOnHostPolicy(tracking.host?.policy) ||
              requiresExplicitNodeEnable(tracking.host);
            const lifecycleCorrelation = isDefaultOnHostPolicy(
              tracking.host?.policy,
            )
              ? mintHostElementLifecycleCorrelation(stableKey)
              : undefined;
            elementArgument = (
              usesHostElementPayload
                ? {
                    ...tracking.host?.page,
                    ...buildHostElementEventOverride(clickTrackingMeta, {
                      ...(tracking.host?.page ?? {}),
                      ...resolvedProps,
                      ...(typeof resolvedProps.variant === 'string'
                        ? { filterFormat: resolvedProps.variant }
                        : {}),
                    }),
                    ...(lifecycleCorrelation ?? {}),
                  }
                : {
                    ...(clickTrackingMeta.elementType !== undefined
                      ? { elementType: clickTrackingMeta.elementType }
                      : {}),
                    ...(clickTrackingMeta.name !== undefined
                      ? { name: clickTrackingMeta.name }
                      : {}),
                    ...(clickTrackingMeta.htmlId !== undefined
                      ? { htmlId: clickTrackingMeta.htmlId }
                      : {}),
                    ...(clickTrackingMeta.location !== undefined
                      ? { location: clickTrackingMeta.location }
                      : {}),
                    ...(clickTrackingMeta.text !== undefined
                      ? { text: clickTrackingMeta.text }
                      : {}),
                  }
            ) as Parameters<
              TrackingContextValue['triggers']['triggerElementClickedEvent']
            >[0];
            builtInKind = 'element';
            composeContext = {
              ...(tracking.eventContext ?? {}),
              ...elementArgument,
            } as EventContext;
          } else if (
            requiresExplicitNodeEnable(tracking.host) ||
            isDefaultOnHostPolicy(tracking.host?.policy)
          ) {
            // Product clicks are step-shaped; an `explicit-node-enable`
            // host has no step, so it stays silent rather than beaming a
            // flow payload.
          } else {
            const stepOverride = buildStepCorrelationOverride(tracking);
            const extraDims = collectClickCustomDimensions(clickTrackingMeta);
            productArgument = {
              ...(tracking.eventContext ?? {}),
              // Spread the per-card product identity over the inherited
              // eventContext (mirrors `Trackable`'s `buildProductOverride`
              // for ProductViewed) so the `ProductClicked` payload carries
              // the real brand / presets / productId / sku / position
              // (and the per-result click correlation) instead of an empty
              // product object. Spread before the authored `_meta.tracking`
              // extras below so an authored `location` still wins over the
              // identity default.
              ...(productContext ?? {}),
              ...(stepOverride.dslConfigId
                ? { dslConfigId: stepOverride.dslConfigId }
                : {}),
              ...(stepOverride.dslConfigVersion !== undefined
                ? { dslConfigVersion: stepOverride.dslConfigVersion }
                : {}),
              // Overrides the inherited `actionOutcome`; authored override wins next.
              ...(resultActionOutcome !== undefined
                ? { actionOutcome: resultActionOutcome }
                : {}),
              ...(clickTrackingMeta.outboundUrl !== undefined
                ? { outboundUrl: clickTrackingMeta.outboundUrl }
                : {}),
              ...(clickTrackingMeta.actionOutcome !== undefined
                ? { actionOutcome: clickTrackingMeta.actionOutcome }
                : {}),
              ...(clickTrackingMeta.location !== undefined
                ? { location: clickTrackingMeta.location }
                : {}),
              ...(clickTrackingMeta.formatType !== undefined
                ? { formatType: clickTrackingMeta.formatType }
                : {}),
              ...(clickTrackingMeta.formatSubtype !== undefined
                ? { formatSubtype: clickTrackingMeta.formatSubtype }
                : {}),
              // `createProductClickedPayload` resolves `product.name` and
              // `product.variant` from `context.product.*` (checked first,
              // via `??`) so affiliate units override the Algolia context.
              ...(clickTrackingMeta.productName !== undefined ||
              clickTrackingMeta.productVariant !== undefined
                ? {
                    product: {
                      ...(clickTrackingMeta.productName !== undefined
                        ? { name: clickTrackingMeta.productName }
                        : {}),
                      ...(clickTrackingMeta.productVariant !== undefined
                        ? { variant: clickTrackingMeta.productVariant }
                        : {}),
                    },
                  }
                : {}),
              // Re-fold per-card identity dims (`resultsSource`); this key reassigns
              // the array and would otherwise clobber the `...productContext` spread.
              customDimensions: mergeProductCustomDimensions(
                stepOverride.customDimensions,
                productContext,
                extraDims,
              ),
            } as Parameters<
              TrackingContextValue['triggers']['triggerProductClickedEvent']
            >[0];
            builtInKind = 'product';
            composeContext = productArgument as EventContext;
          }
        } catch (err) {
          reportBoundRenderError(
            err,
            `binding-enhancer:click-dispatch:${clickTrackingMeta.kind}`,
          );
        }
      }
      // Step correlation exists on a flow. A `registered-defaults` or
      // `explicit-node-enable` host is step-free: both always populate a
      // defined `stepCorrelation` (zeroed sentinel fields, never
      // `undefined`), so `tracking.stepCorrelation !== undefined` is true
      // there too and does not tell step-free apart from a real flow step.
      // `buildStepCorrelationOverride` then stamps a literal `stepIndex: '0'`
      // dimension onto a host that has no step at all. Gate on the host
      // policy directly instead.
      const isStepFreeHost =
        tracking !== undefined &&
        (requiresExplicitNodeEnable(tracking.host) ||
          isDefaultOnHostPolicy(tracking.host?.policy));
      // A node with no bindings never needs this object, so the built-in
      // click stays on its old path.
      const fallbackContext = (
        tracking && slotBindings.length > 0 && composeContext === undefined
          ? {
              ...(tracking.eventContext ?? {}),
              ...(isStepFreeHost ? {} : buildStepCorrelationOverride(tracking)),
            }
          : {}
      ) as EventContext;
      const sends: ComposedClickSend[] = [];
      for (const ready of slotBindings) {
        try {
          const send = composeReadyClick(
            ready,
            clickBindingScope,
            clickBindingScope.experience,
            composeContext ?? fallbackContext,
          );
          if (send) sends.push(send);
        } catch (err) {
          reportBoundRenderError(
            err,
            `binding-enhancer:configured-event:${ready.binding.id}`,
          );
        }
      }
      let replacedElement = false;
      let replacedProduct = false;
      for (const send of sends) {
        if (send.name === 'ElementClicked') replacedElement = true;
        if (send.name === 'ProductClicked') replacedProduct = true;
      }
      // `_action` navigation that advances the step fires `useHandleStepForward`
      // on this same click, independently of this dispatch, for whatever
      // component owns the click. On the entry step's first forward click
      // that hook's firer (`useForwardStepEventFiring`) also sends
      // `ProductClicked` — it has no visibility into whether this click's
      // built-in product click was just suppressed or replaced by a
      // configured binding. Signal it via the shared ref so the two do not
      // both send for the same click.
      //
      // Decide from what happened on *this* click. A configured
      // `ProductClicked` replaces the entry send even when the node
      // never declared `kind: 'product'`. `defaults` suppress signals
      // the firer only when the silenced built-in is a product click,
      // or when no kind is declared. `kind: 'element'` suppress silences
      // `ElementClicked` and leaves the entry `ProductClicked` alone.
      // The declared slice kind is the source. On a flow host
      // `clickTrackingMeta` stays empty for `element`, and `builtInKind`
      // stays unset when suppress skips the payload block.
      //
      // Every forward-owning click writes the ref, `true` or `false`.
      // A sibling control with no navigation action never writes it.
      // Validation failure clears the ref after this write. The TCPA
      // gate does not: that attempt is still open, so a later forward
      // that does not pass through this write can still read `true`.
      const isClickOwnerSlot =
        name === clickPropName ||
        (useWrapperSlot && name === TRACKING_WRAPPER_CLICK_SLOT);
      if (
        tracking?.entryProductClickSuppressedRef &&
        isClickOwnerSlot &&
        ownsForwardNavigation
      ) {
        tracking.entryProductClickSuppressedRef.current =
          replacedProduct ||
          (suppressBuiltIn && declaredClickKind !== 'element');
      }
      if (
        isClickDispatchSlot &&
        tracking &&
        clickTrackingMeta &&
        !suppressBuiltIn
      ) {
        try {
          if (
            builtInKind === 'element' &&
            elementArgument &&
            !replacedElement
          ) {
            tracking.triggers.triggerElementClickedEvent(elementArgument);
          } else if (
            builtInKind === 'product' &&
            productArgument &&
            !replacedProduct
          ) {
            tracking.triggers.triggerProductClickedEvent(productArgument);
          }
        } catch (err) {
          reportBoundRenderError(
            err,
            `binding-enhancer:click-dispatch:${clickTrackingMeta.kind}`,
          );
        }
      }
      if (tracking) {
        for (const send of sends) {
          try {
            tracking.sendConfiguredEvent(send.name, send.payload);
          } catch (err) {
            reportBoundRenderError(
              err,
              `binding-enhancer:configured-event:${send.bindingId}`,
            );
          }
        }
      }
    };
  }
  return out;
}

/**
 * `ProductClicked` customDimensions for the DSL click dispatcher.
 * Mirrors `Trackable`'s `collectProductCustomDimensions` (ctaText /
 * outboundUrl / actionOutcome) so the view and click events for the
 * same DSL node carry matching informational metadata on the wire.
 * Kept inline here (rather than shared with `Trackable`) to avoid an
 * extra module boundary; the two surfaces have intentionally
 * identical contracts because they're the two halves of the product
 * event family.
 */
function collectClickCustomDimensions(
  clickMeta: ProductClickTrackingMeta,
): Array<{ key: string; value: string }> {
  const out: Array<{ key: string; value: string }> = [];
  if (clickMeta.ctaText) out.push({ key: 'ctaText', value: clickMeta.ctaText });
  if (clickMeta.outboundUrl) {
    out.push({ key: 'outboundUrl', value: clickMeta.outboundUrl });
  }
  if (clickMeta.actionOutcome) {
    out.push({ key: 'actionOutcome', value: clickMeta.actionOutcome });
  }
  return out;
}

/**
 * Pick the event prop name where the `formKey` capture wrapper attaches.
 * Priority order:
 *   1. The first `eventPropNames` entry that already has an
 *      `_action.<event>` handler (so the capture composes naturally
 *      with the DSL-declared analytics / nav action).
 *   2. The first entry of `eventPropNames` (default: `onChange` —
 *      matches the standard controlled-input pattern).
 */

function pickFormKeyTarget(
  eventPropNames: readonly string[],
  actionHandlers: Readonly<Record<string, EventHandler>>,
): string | undefined {
  return (
    eventPropNames.find((name) => name in actionHandlers) ?? eventPropNames[0]
  );
}

interface AuthoredHandler {
  readonly fn: EventHandler;
  readonly origin: 'static' | 'resolved';
}

/**
 * Collect every authored handler registered for `eventName` in
 * precedence order: static catalog default first, then the
 * resolved/raw handler from the host (a `_bind.<event>` that
 * produced a function, or a passthrough handler from Puck). Both
 * sources run when both are present — do not choose; compose.
 * Non-function entries are ignored — a bind that resolves to
 * `undefined` must not paper over a perfectly good static handler,
 * and vice versa. The `origin` tag is only used to make error logs
 * actionable.
 */
function collectAuthoredHandlers(
  eventName: string,
  resolvedProps: Readonly<Record<string, unknown>>,
  staticProps: Readonly<Record<string, unknown>> | undefined,
): readonly AuthoredHandler[] {
  const out: AuthoredHandler[] = [];
  const fromStatic = staticProps?.[eventName];
  if (typeof fromStatic === 'function') {
    out.push({ fn: fromStatic as EventHandler, origin: 'static' });
  }
  const fromResolved = resolvedProps[eventName];
  if (typeof fromResolved === 'function') {
    out.push({ fn: fromResolved as EventHandler, origin: 'resolved' });
  }
  return out;
}

/**
 * Internal flag mirrored on bound components so {@link createBoundRender}
 * and {@link applyBindings} can short-circuit double-wrapping. **Not**
 * part of the public surface — exposed here only so unit tests in
 * `applyBindings.test.ts` can pre-stamp a component as already-bound;
 * the binding-enhancer barrel intentionally omits this re-export.
 */
export const __BOUND_RENDER_FLAG = BOUND_RENDER_FLAG;

/**
 * Pull every {@link isSyntheticActionEvent} handler out of the
 * `_action.*` map produced by `resolveActions` and return them
 * separately, leaving `actionHandlers` purged of synthetic entries.
 *
 * Why this exists: synthetic events (today: `onSourceChange`) are
 * fired by the binding-enhancer itself when the watched data
 * sources transition. They are NOT React/DOM events. If we left
 * them in `actionHandlers`, `composeEventHandlers` would wire them
 * onto the underlying component as a prop — landing
 * `onSourceChange={fn}` on a `<Select>` is at best a noisy unknown
 * prop and at worst (depending on the catalog component) a
 * crashing one. Centralising the filter here also keeps the
 * synthetic-event contract local to this module so future synthetic
 * events (`onResolve`, `onError`, …) opt in by appending to
 * {@link SYNTHETIC_ACTION_EVENTS} alone.
 *
 * Mutates the input map in place — callers pass the
 * `actionHandlers` they're about to consume; mutation is safe
 * because `resolveActions` returns a fresh map per render.
 */
function extractSyntheticHandlers(
  actionHandlers: Record<string, (...args: unknown[]) => void>,
): Record<string, (...args: unknown[]) => void> {
  const out: Record<string, (...args: unknown[]) => void> = {};
  for (const eventName of Object.keys(actionHandlers)) {
    if (isSyntheticActionEvent(eventName)) {
      out[eventName] = actionHandlers[eventName];
      delete actionHandlers[eventName];
    }
  }
  return out;
}

/**
 * Module-level store of per-component "last-seen settled snapshot"
 * maps, keyed by the DSL-derived `stableKey`
 * (`<flowSessionId>::<dslConfigId>::<stepIndex>::<puckNodeId>`). Mirrors the
 * `FIRED_STABLE_KEYS` pattern in {@link Trackable} and exists for the
 * same reason: in the production / dev-harness render path, ancestor
 * re-renders driven by DSM `formState` and `sources` updates routinely
 * tear down and re-create the entire bound subtree (Strict Mode
 * amplifies this in development with intentional unmount+remount).
 * A per-instance `useRef(new Map())` is recreated on every remount,
 * which resets the "first transition is silent" gate — so every
 * post-remount settle is misread as a first settle, and the
 * cascading-select reset (`_action.onSourceChange: formState.clear`)
 * never fires.
 *
 * Pinning the snapshot map to a stable, DSL-derived identity instead
 * of a React-instance ref lets the gate fire-once-per-logical-node
 * regardless of how many times React mounts that node within the
 * session. The composition of `stableKey` is owned by
 * {@link BoundComponent} and intentionally shared with
 * {@link Trackable} so the two dedup surfaces stay aligned.
 *
 * Tests that need a clean slate between cases can call
 * {@link resetOnSourceChangeSnapshots}.
 */
const ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS = new Map<
  string,
  Map<string, unknown>
>();

/**
 * Test-only escape hatch for clearing the module-scoped settled
 * snapshot map. Production callers should never invoke this — the
 * snapshot survives across remounts on purpose. Exported for parity
 * with `__resetTrackableFired` in {@link Trackable}.
 */
export function resetOnSourceChangeSnapshots(): void {
  ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS.clear();
}

/**
 * Watch a bound component's referenced data sources and dispatch
 * the author-declared `_action.onSourceChange` handler whenever any
 * of them settles to a NEW value after the first settled snapshot.
 *
 * Backs the cascading-select reset pattern via the SAD's Action
 * Type Registry — authors compose:
 *
 *   `_action.onSourceChange`: { type: 'formState.clear',
 *                                params: { keys: ['{{this.formKey}}'] } }
 *
 * and the host-supplied dispatcher decides what `formState.clear`
 * actually does. The binding-enhancer is the WHEN; the action
 * registry is the WHAT. Adding a new "on source settle" side
 * effect (e.g. fire a tracking event, clear other slots) is
 * registry-only — no changes to this hook.
 *
 * Why a "settled-once" gate instead of a plain "data changed" check:
 *   - Initial load: a source's data goes `undefined` → first
 *     resolved/empty/error response. That's not a user-initiated
 *     change — firing here would dispatch a clobbering action over
 *     values restored from URL params, drafts, or the host's
 *     `initialFormState`. So we record the first settled snapshot
 *     silently, and only fire on the SECOND-AND-LATER transition.
 *   - `pending` is not a settled state: re-fetches transiently flip
 *     to `pending` on their way to `resolved`; recording during
 *     pending would compare against an in-flight nullish snapshot
 *     and produce a phantom dispatch when the real response arrives.
 *
 * Survives remounts: the settled-snapshot store is module-scoped and
 * keyed by `stableKey` (see {@link ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS}).
 * When `stableKey` is undefined (legacy/test paths with no
 * `<TrackingProvider>` or no Puck node id), the hook falls back to a
 * per-instance `useRef` so existing call sites without a stable
 * identity see no behavioural change.
 *
 * Stable-deps note: `referencedSources` is a fresh `Set` on every
 * render (the resolver builds it from scratch). To avoid re-running
 * the effect on every render even when membership is unchanged, we
 * key the effect on a sorted, joined string of the source names —
 * the same membership produces the same key, so the effect's deps
 * stay stable for a given DSL configuration. The handler reference
 * also changes every render (resolveActions builds a new closure),
 * so we mirror it through a ref and read `.current` inside the
 * effect rather than depending on the function identity.
 */
function useOnSourceChange(args: {
  readonly handler: ((...args: unknown[]) => void) | undefined;
  readonly referencedSources: ReadonlySet<string>;
  readonly sources: Readonly<Record<string, ResolvedDataSource>>;
  /**
   * DSL-derived stable identity used to pin the settled snapshot to a
   * key that survives ancestor remounts (Strict Mode +
   * formState-driven re-render churn). Composed the same way as
   * {@link Trackable}'s `stableKey`:
   * `<flowSessionId>::<dslConfigId>::<stepIndex>::<puckNodeId>`. When absent (legacy
   * / ad-hoc tests without `<TrackingProvider>` or without a Puck
   * node id) the hook falls back to a per-instance `useRef`.
   */
  readonly stableKey: string | undefined;
}): void {
  const { handler, referencedSources, sources, stableKey } = args;

  /**
   * Per-instance fallback snapshot map. Only consulted when no
   * `stableKey` is supplied. New `Map` per instance — for legacy
   * callers this preserves the pre-fix behaviour exactly.
   */
  const fallbackSettledRef = useRef<Map<string, unknown>>(new Map());

  /**
   * The latest action handler. Mirrored through a ref so the effect
   * can call `handlerRef.current()` without depending on the
   * handler's function identity (which churns every render because
   * `resolveActions` returns fresh closures). Without this, every
   * render would invalidate the effect deps and re-run the body —
   * silently re-recording the settled snapshot and missing the
   * first real transition.
   */
  const handlerRef = useRef<typeof handler>(undefined);
  handlerRef.current = handler;

  // Materialise `referencedSources` as an Array up-front so the
  // effect can iterate it with a plain index loop. This package
  // compiles to ES5 without `downlevelIteration`, so a literal
  // `for…of <Set>` silently iterates zero times — the same trap
  // documented on `composeEventHandlers` further up. The sorted+
  // joined form doubles as the stable-deps key (same membership →
  // same string), so the effect doesn't re-run when only sources we
  // don't depend on change.
  const referencedNames = Array.from(referencedSources).sort();
  const referencedKey = referencedNames.join('|');

  useEffect(() => {
    if (referencedNames.length === 0) {
      // No source to watch — the synthetic event has nothing to
      // fire on. We deliberately do NOT warn here; an author may
      // attach `_action.onSourceChange` to a component whose only
      // bound source resolves on a different render path, and the
      // warn would spam logs during transient states.
      return;
    }

    // Resolve the snapshot map: module-scoped + stable-keyed when
    // possible so the gate survives remounts, per-instance ref
    // otherwise. Lookup is O(1); the map is created lazily so an
    // un-used stableKey doesn't allocate.
    let seen: Map<string, unknown>;
    if (stableKey) {
      let scoped = ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS.get(stableKey);
      if (!scoped) {
        scoped = new Map();
        ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS.set(stableKey, scoped);
      }
      seen = scoped;
    } else {
      seen = fallbackSettledRef.current;
    }

    let anyChanged = false;
    for (let i = 0; i < referencedNames.length; i++) {
      const name = referencedNames[i];
      const state = sources[name];
      if (!state) continue;
      // `pending` is not a settled state. Skip without recording so
      // the next settled transition is compared against the *prior*
      // settled snapshot, not against the in-flight nullish value.
      if (state.status === 'pending') continue;
      if (seen.has(name)) {
        if (seen.get(name) !== state.data) anyChanged = true;
      }
      // Record (or update) the latest settled reference. First-seen
      // entries silently populate the map without firing — that's
      // the "first transition is silent" half of the gate.
      seen.set(name, state.data);
    }

    if (anyChanged) {
      const fn = handlerRef.current;
      if (!fn) return;
      try {
        // Synthetic events have no caller-supplied event arg; the
        // action handler treats `event` as undefined when it
        // interpolates `{{event.*}}` templates.
        fn();
      } catch (err) {
        reportBoundRenderError(err, 'binding-enhancer:_action.onSourceChange');
      }
    }
    // `referencedNames` is reflected in `referencedKey`; including
    // the array directly would re-run the effect on every render
    // (the Array.from result is fresh per resolver call) for no
    // behavioural gain. `handlerRef` stabilises the handler dep —
    // see the doc on `handlerRef`. `stableKey` is included so a
    // re-keyed instance (e.g. node id changes between renders)
    // re-resolves the snapshot map cleanly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencedKey, sources, stableKey]);
}
